import React, { useState, useEffect, useRef, Component } from "react";
import { useSearchParams } from "react-router-dom";
import { logisticsAPI, usersAPI, routePlannerAPI, logisticsEntitiesAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../hooks/useConfirm";
import {
  Plus, Edit, Trash2, X, Truck, Map, Users, Download, Upload, Search,
  School, BarChart2, RefreshCw, Calendar, ArrowRightLeft, FileText,
  ChevronRight, Phone, Mail, MapPin, Info, Wrench, Fuel, Loader, CheckCircle,
  AlertTriangle, Settings, Navigation,
} from "lucide-react";
import { exportCSV, parseCSV } from "../utils/csvUtils";


/** Isolated error boundary — a crash in one tab shows an inline message, never takes down the whole module. */
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === "development") {
      console.error("Logistics tab error:", error, info);
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-[#FBEAE7] border border-[#C2624E]/20 rounded-2xl px-6 py-8 text-center space-y-3">
          <div className="w-12 h-12 bg-[#C2624E]/10 rounded-full flex items-center justify-center mx-auto">
            <span className="text-[#C2624E] font-bold text-xl">!</span>
          </div>
          <p className="font-bold text-[#C2624E]">This tab encountered an error</p>
          <p className="text-sm text-[#8A7E73] max-w-sm mx-auto">
            {this.state.error?.message || "An unexpected error occurred in this section."}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="text-sm text-[#E8956D] font-bold hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}


// ─── MAINTENANCE TAB COMPONENT ────────────────────────────────────────────────
function MaintenanceTab({ routes, kitchens, selectedKitchen }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ vehicle_number: "", maintenance_type: "routine_service", date: new Date().toISOString().split("T")[0], odometer: "", cost: "", description: "", service_provider: "", next_service_date: "" });
  const [vehicles, setVehicles] = useState([]);
  const TYPES = ["routine_service","repair","tyre_change","oil_change","accident","inspection","other"];

  useEffect(() => {
    logisticsAPI.getVehicles().then(r => setVehicles(r.data || [])).catch(() => {});
    fetchRecords();
  }, [selectedKitchen]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const r = await logisticsAPI.getMaintenance({ kitchen_id: selectedKitchen || null });
      setRecords(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      await logisticsAPI.createMaintenance({ ...form, kitchen_id: selectedKitchen || null, odometer: form.odometer ? parseFloat(form.odometer) : null, cost: form.cost ? parseFloat(form.cost) : null });
      setShowForm(false); setForm({ vehicle_number: "", maintenance_type: "routine_service", date: new Date().toISOString().split("T")[0], odometer: "", cost: "", description: "", service_provider: "", next_service_date: "" });
      fetchRecords();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-[#3A322C]">Vehicle Maintenance Log</h2>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={14} /> Add Entry</button>
      </div>
      {showForm && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))}>
                <option value="">Select Vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
                <option value="other">Other (type below)</option>
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Type</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.maintenance_type} onChange={e => setForm(f => ({ ...f, maintenance_type: e.target.value }))}>
                {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Cost (₹)</label>
              <input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Odometer (km)</label>
              <input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.odometer} onChange={e => setForm(f => ({ ...f, odometer: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Next Service Date</label>
              <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.next_service_date} onChange={e => setForm(f => ({ ...f, next_service_date: e.target.value }))} /></div>
            <div className="col-span-2"><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Description *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Service Provider</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.service_provider} onChange={e => setForm(f => ({ ...f, service_provider: e.target.value }))} /></div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={handleSave} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2 text-sm font-bold">Save</button>
          </div>
        </div>
      )}
      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F3F0]"><tr>{["Date","Vehicle","Type","Description","Cost","Next Service","Service By"].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#F4F3F0]">
              {records.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8A7E73]">No maintenance records</td></tr>}
              {records.map(r => (
                <tr key={r.id} className="hover:bg-[#FAFAF8]">
                  <td className="px-4 py-3 font-bold text-[#3A322C]">{r.date}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#5C5147]">{r.vehicle_number}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full capitalize">{r.maintenance_type?.replace(/_/g," ")}</span></td>
                  <td className="px-4 py-3 text-[#3A322C] max-w-[150px] truncate">{r.description}</td>
                  <td className="px-4 py-3 font-bold text-[#E8956D]">{r.cost ? `₹${r.cost}` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#8A7E73]">{r.next_service_date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#8A7E73]">{r.service_provider || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── FUEL LOG TAB COMPONENT ───────────────────────────────────────────────────
function FuelTab({ routes, kitchens, selectedKitchen, vehicles }) {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ vehicle_number: "", date: new Date().toISOString().split("T")[0], fuel_type: "diesel", quantity_litres: "", cost: "", odometer: "" });
  const [allVehicles, setAllVehicles] = useState(vehicles || []);

  useEffect(() => {
    if (vehicles?.length > 0) setAllVehicles(vehicles);
    else logisticsAPI.getVehicles().then(r => setAllVehicles(r.data || [])).catch(() => {});
    fetchLogs();
  }, [selectedKitchen]);

  const fetchLogs = async () => {
    try {
      const [l, s] = await Promise.all([
        logisticsAPI.getFuelLog({ kitchen_id: selectedKitchen || null }),
        logisticsAPI.getFuelSummary({ kitchen_id: selectedKitchen || null })
      ]);
      setLogs(l.data || []);
      setSummary(s.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleSave = async () => {
    try {
      await logisticsAPI.createFuelLog({ ...form, kitchen_id: selectedKitchen || null, quantity_litres: parseFloat(form.quantity_litres), cost: parseFloat(form.cost), odometer: form.odometer ? parseFloat(form.odometer) : null });
      setShowForm(false); setForm({ vehicle_number: "", date: new Date().toISOString().split("T")[0], fuel_type: "diesel", quantity_litres: "", cost: "", odometer: "" });
      fetchLogs();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-[#3A322C]">Fuel Log</h2>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"><Plus size={14} /> Add Entry</button>
      </div>
      {summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summary.slice(0, 4).map(s => (
            <div key={s.vehicle} className="bg-white border border-[#E5E1DB] rounded-xl p-3">
              <p className="font-mono text-xs font-bold text-[#5C5147] mb-1">{s.vehicle}</p>
              <p className="text-lg font-extrabold text-[#E8956D]">₹{s.total_cost?.toLocaleString()}</p>
              <p className="text-xs text-[#8A7E73]">{s.total_litres}L · {s.fill_count} fills</p>
            </div>
          ))}
        </div>
      )}
      {showForm && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))}>
                <option value="">Select</option>
                {allVehicles.map(v => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Litres *</label>
              <input type="number" step="0.1" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.quantity_litres} onChange={e => setForm(f => ({ ...f, quantity_litres: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Cost (₹) *</label>
              <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Odometer (km)</label>
              <input type="number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={form.odometer} onChange={e => setForm(f => ({ ...f, odometer: e.target.value }))} /></div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2 text-sm font-bold text-[#5C5147]">Cancel</button>
            <button onClick={handleSave} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2 text-sm font-bold">Save</button>
          </div>
        </div>
      )}
      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F3F0]"><tr>{["Date","Vehicle","Litres","Cost","Odometer","Mileage","Logged By"].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#F4F3F0]">
              {logs.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8A7E73]">No fuel records</td></tr>}
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-[#FAFAF8]">
                  <td className="px-4 py-3 font-bold text-[#3A322C]">{l.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.vehicle_number}</td>
                  <td className="px-4 py-3 font-bold text-[#5C5147]">{l.quantity_litres}L</td>
                  <td className="px-4 py-3 font-bold text-[#E8956D]">₹{l.cost}</td>
                  <td className="px-4 py-3 text-xs text-[#8A7E73]">{l.odometer ? `${l.odometer} km` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#637C64] font-bold">{l.mileage ? `${l.mileage} km/L` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#8A7E73]">{l.created_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const tabs = [
  { id: "route_master",      label: "Route Master",       icon: Map,            subKey: "logistics_routes"       },
  { id: "routes",            label: "Vehicles Assigned",  icon: Truck,          subKey: "logistics_routes"       },
  { id: "school_assignment", label: "School Assignment",  icon: School,         subKey: "logistics_routes"       },
  { id: "coordinators",      label: "Route Coordinators", icon: Users,          subKey: "logistics_coordinators" },
  { id: "schools",           label: "School Master",      icon: School,         subKey: "logistics_schools"      },
  { id: "vehicles_drivers",  label: "Vehicles & Drivers", icon: Truck,          subKey: "logistics_routes"       },
  { id: "temp",              label: "Temp Assignments",   icon: ArrowRightLeft, subKey: "logistics_temp"         },
  { id: "maintenance",       label: "Vehicle Maintenance",icon: FileText,       subKey: "logistics_maintenance"  },
  { id: "fuel",              label: "Fuel Log",           icon: FileText,       subKey: "logistics_fuel"         },
  { id: "reconciliation",    label: "Reconciliation",     icon: RefreshCw,      subKey: null                     },
  { id: "reports",           label: "Reports",            icon: BarChart2,      subKey: null                     },
];

const COORDINATORS = ["VIJAY", "AJAY", "ARAVIND", "SHIVA", "KIRAN", "KRISHNA", "YADAGIRI", "ZAKER"];

export default function Logistics() {
  const { kitchens: ctxKitchens, selectedKitchen } = useKitchen();
  const { user: currentUser } = useAuth();   // ← logged-in user
  const isAdmin = currentUser?.is_admin;     // ← only super admin sees user dropdown
  const { confirm, ConfirmModal } = useConfirm();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    // Normalise hyphenated URL params to underscore tab IDs
    return t ? t.replace(/-/g, "_") : "route_master";
  });
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t.replace(/-/g, "_"));
  }, [searchParams]);
  // Fallback: if active tab is hidden by permissions, switch to first visible tab
  useEffect(() => {
    const visible = tabs.filter(t => !t.subKey || currentUser?.is_admin || currentUser?.permissions?.[`sub_${t.subKey}_view`] !== false);
    if (visible.length > 0 && !visible.find(t => t.id === activeTab)) setActiveTab(visible[0].id);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Routes/Vehicles/Coordinators state
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // School Master state
  const [schools, setSchools] = useState([]);
  const [schoolTotal, setSchoolTotal] = useState(0);
  const [schoolStats, setSchoolStats] = useState(null);
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState({ route_number: "", coordinator: "", mandal: "", area: "" });
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolUploadLoading, setSchoolUploadLoading] = useState(false);
  const [schoolUploadResult, setSchoolUploadResult] = useState(null);
  const schoolUploadRef = useRef(null);
  // Map pin state
  const [mapPinModal, setMapPinModal] = useState(null); // school object being pinned
  const [mapPinLoading, setMapPinLoading] = useState(false);
  const [mapPinMsg, setMapPinMsg] = useState("");

  // Temp Assignments state
  const [tempAssignments, setTempAssignments] = useState([]);
  const [tempForm, setTempForm] = useState({ date: new Date().toISOString().split("T")[0], original_coordinator: "", replacement_coordinator: "", route_numbers: [], reason: "", notes: "" });
  const [tempLoading, setTempLoading] = useState(false);
  const [showTempForm, setShowTempForm] = useState(false);
  const [editTempId, setEditTempId] = useState(null);

  // Detail drawer state
  const [detailDrawer, setDetailDrawer] = useState(null); // {type: "coordinator"|"route", data: obj}
  const [expandedRows, setExpandedRows] = useState({}); // {coordinatorId: true/false}

  const toggleExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const openDetail = (type, data) => setDetailDrawer({ type, data });
  const [reportFrom, setReportFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0];
  });
  const [reportTo, setReportTo] = useState(new Date().toISOString().split("T")[0]);
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, v, c, u] = await Promise.all([
        logisticsAPI.getRoutes(),
        logisticsAPI.getVehicles(),
        logisticsAPI.getCoordinators(),
        usersAPI.getAll(),
      ]);
      setRoutes(r.data || []);
      setVehicles(v.data || []);
      setCoordinators(c.data || []);
      setUsers(u.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchSchools = async (params = {}) => {
    setSchoolLoading(true);
    try {
      const p = { limit: 500, ...schoolFilter, ...params };
      if (schoolSearch) p.search = schoolSearch;
      Object.keys(p).forEach(k => { if (!p[k]) delete p[k]; });
      const res = await logisticsAPI.getSchoolMaster(p);
      setSchools(res.data?.records || []);
      setSchoolTotal(res.data?.total || 0);
      if (!schoolStats) {
        const st = await logisticsAPI.getSchoolMasterStats();
        setSchoolStats(st.data);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setSchoolLoading(false);
  };

  const fetchTempAssignments = async () => {
    setTempLoading(true);
    try {
      const res = await logisticsAPI.getTempAssignments();
      setTempAssignments(res.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setTempLoading(false);
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const res = await logisticsAPI.getDailyReport({ from_date: reportFrom, to_date: reportTo });
      setReportData(res.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setReportLoading(false);
  };

  useEffect(() => {
    if (activeTab === "schools") fetchSchools();
    if (activeTab === "temp") fetchTempAssignments();
    if (activeTab === "reports") fetchReport();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route/Vehicle/Coordinator handlers
  const openCreate = (type) => {
    // For coordinators: non-admin users auto-populate their own details
    if (type === "coordinator" && !isAdmin) {
      setForm({
        user_id: currentUser?._id || currentUser?.id || "",
        coordinator_name: currentUser?.name || "",
      });
    } else {
      setForm({});
    }
    setModal({ type, mode: "create" });
    setError("");
  };
  const openEdit = (type, item) => { setForm({ ...item }); setModal({ type, mode: "edit", id: item.id }); setError(""); };
  const openAssign = (route) => { setForm({ route_id: route.id, vehicle_id: route.assigned_vehicle_id || "", is_temp: false }); setModal({ type: "assign", route }); setError(""); };

  const handleSave = async () => {
    setError("");
    try {
      if (modal.type === "route") {
        if (modal.mode === "create") await logisticsAPI.createRoute(form);
        else await logisticsAPI.updateRoute(modal.id, form);
      } else if (modal.type === "vehicle") {
        if (modal.mode === "create") await logisticsAPI.createVehicle(form);
        else await logisticsAPI.updateVehicle(modal.id, form);
      } else if (modal.type === "coordinator") {
        if (modal.mode === "create") await logisticsAPI.createCoordinator({ ...form, route_ids: form.route_ids || [], permanent_route_ids: form.permanent_route_ids || [] });
        else await logisticsAPI.updateCoordinator(modal.id, form);
      } else if (modal.type === "assign") {
        await logisticsAPI.assignVehicle(form.route_id, { vehicle_id: form.vehicle_id, is_temp: form.is_temp });
      } else if (modal.type === "school") {
        await logisticsAPI.updateSchool(modal.id, form);
      }
      setModal(null);
      fetchAll();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (type, id) => {
    if (!await confirm("Delete?")) return;
    try {
      if (type === "route") await logisticsAPI.deleteRoute(id);
      else if (type === "vehicle") await logisticsAPI.deleteVehicle(id);
      else if (type === "coordinator") await logisticsAPI.deleteCoordinator(id);
      fetchAll();
    } catch (e) { alert(formatError(e)); }
  };

  const handleTempSave = async () => {
    try {
      if (editTempId) await logisticsAPI.updateTempAssignment(editTempId, tempForm);
      else await logisticsAPI.createTempAssignment(tempForm);
      setShowTempForm(false);
      setEditTempId(null);
      setTempForm({ date: new Date().toISOString().split("T")[0], original_coordinator: "", replacement_coordinator: "", route_numbers: [], reason: "", notes: "" });
      fetchTempAssignments();
    } catch (e) { alert(formatError(e)); }
  };

  const handleDeleteTemp = async (id) => {
    if (!await confirm("Delete this temporary assignment?")) return;
    await logisticsAPI.deleteTempAssignment(id);
    fetchTempAssignments();
  };

  const getKitchenName = (id) => kitchens.find(k => k.id === id)?.name || "—";
  const getVehicleNum = (id) => vehicles.find(v => v.id === id)?.vehicle_number || "—";

  const filteredRoutes = routes.filter(r => !search || r.route_number?.toLowerCase().includes(search.toLowerCase()) );
  const filteredVehicles = vehicles.filter(v => !search || v.vehicle_number?.toLowerCase().includes(search.toLowerCase()));
  const filteredCoords = coordinators.filter(c => !search || c.coordinator_name?.toLowerCase().includes(search.toLowerCase()));

  const exportSchools = () => {
    exportCSV(schools.map(s => ({
      route_number: s.route_number,
      school_code: s.school_code || "",
      coordinator: s.coordinator_name,
      mandal: s.mandal,
      school_name: s.school_name,
      area: s.area,
    })), "school_master");
  };

  const handleSchoolUpload = async (file) => {
    if (!file) return;
    setSchoolUploadLoading(true);
    setSchoolUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await logisticsAPI.bulkUploadSchoolMaster(fd);
      setSchoolUploadResult({ success: true, ...res.data });
      setSchoolStats(null); // force stats refresh
      fetchSchools();
    } catch (e) {
      setSchoolUploadResult({ success: false, error: formatError(e) });
    }
    setSchoolUploadLoading(false);
    if (schoolUploadRef.current) schoolUploadRef.current.value = "";
  };

  // Google Maps loader (singleton)
  const gmapsLoadedRef = useRef(false);
  const loadGoogleMapsForPin = (cb) => {
    if (gmapsLoadedRef.current && window.google?.maps) { cb(); return; }
    if (window.google?.maps) { gmapsLoadedRef.current = true; cb(); return; }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) { existing.onload = () => { gmapsLoadedRef.current = true; cb(); }; return; }
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.onload = () => { gmapsLoadedRef.current = true; cb(); };
    document.head.appendChild(s);
  };

  const mapContainerRef = useRef(null);
  const gmapInstanceRef = useRef(null);
  const pinMarkerRef = useRef(null);
  const [pendingCoords, setPendingCoords] = useState(null); // {lat, lng}

  const openMapPin = (school) => {
    setMapPinModal(school);
    setMapPinMsg("");
    setPendingCoords(school.lat && school.lng ? { lat: parseFloat(school.lat), lng: parseFloat(school.lng) } : null);
    // Map init happens in useEffect watching mapPinModal
  };

  useEffect(() => {
    if (!mapPinModal) { gmapInstanceRef.current = null; pinMarkerRef.current = null; return; }
    // FIX 4: Google Maps loaded at root via index.html — just wait for it
    const initMap = () => {
      if (!mapContainerRef.current) return;
      const center = mapPinModal.lat && mapPinModal.lng
        ? { lat: parseFloat(mapPinModal.lat), lng: parseFloat(mapPinModal.lng) }
        : { lat: 17.3850, lng: 78.4867 };
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center, zoom: mapPinModal.lat ? 15 : 11, mapTypeId: "satellite",
      });
      gmapInstanceRef.current = map;
      if (mapPinModal.lat && mapPinModal.lng) {
        pinMarkerRef.current = new window.google.maps.Marker({ position: center, map, draggable: true });
        pinMarkerRef.current.addListener("dragend", (e) => setPendingCoords({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      }
      map.addListener("click", (e) => {
        const lat = e.latLng.lat(), lng = e.latLng.lng();
        setPendingCoords({ lat, lng });
        if (pinMarkerRef.current) pinMarkerRef.current.setPosition({ lat, lng });
        else {
          pinMarkerRef.current = new window.google.maps.Marker({ position: { lat, lng }, map, draggable: true });
          pinMarkerRef.current.addListener("dragend", (ev) => setPendingCoords({ lat: ev.latLng.lat(), lng: ev.latLng.lng() }));
        }
      });
    };

    if (window.google?.maps) {
      setTimeout(initMap, 100);
    } else {
      // wait for the script to load
      const check = setInterval(() => {
        if (window.google?.maps) { clearInterval(check); setTimeout(initMap, 100); }
      }, 200);
      setTimeout(() => clearInterval(check), 10000);
    }
  }, [mapPinModal]); // eslint-disable-line

  const saveMapPin = async () => {
    if (!pendingCoords || !mapPinModal) return;
    setMapPinLoading(true); setMapPinMsg("");
    try {
      await logisticsAPI.setSchoolLocation(mapPinModal.id, pendingCoords);
      setMapPinMsg("Coordinates saved!");
      fetchSchools();
      setTimeout(() => { setMapPinModal(null); setMapPinMsg(""); }, 1200);
    } catch (e) { setMapPinMsg("Error: " + formatError(e)); }
    setMapPinLoading(false);
  };



  const exportReport = () => {
    exportCSV(reportData.map(r => ({
      date: r.date,
      original_coordinator: r.original_coordinator,
      replacement_coordinator: r.replacement_coordinator,
      routes: (r.route_numbers || []).join(", "),
      reason: r.reason || "",
      notes: r.notes || "",
      created_by: r.created_by || "",
    })), `logistics_report_${reportFrom}_${reportTo}`);
  };

  const ModalForm = () => {
    if (!modal) return null;
    if (modal.type === "route") return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Number *</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. 1, 5, 70" value={form.route_number || ""} onChange={e => setForm({ ...form, route_number: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Coordinator Name</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. ZAKEER, PAVAN" value={form.coordinator_name || ""} onChange={e => setForm({ ...form, coordinator_name: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Driver Name</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. AZEEM" value={form.driver_name || ""} onChange={e => setForm({ ...form, driver_name: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. TS07UB0497" value={form.driver_vehicle_number || ""} onChange={e => setForm({ ...form, driver_vehicle_number: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Driver Contact</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. 9876543210" value={form.driver_contact || ""} onChange={e => setForm({ ...form, driver_contact: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
            <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value })}>
              <option value="">Select Kitchen</option>
              {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select></div>
        </div>
      </div>
    );
    if (modal.type === "school") return (
      <div className="space-y-4">
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name</label>
          <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.school_name || ""} onChange={e => setForm({ ...form, school_name: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Code</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm font-mono outline-none focus:border-[#E8956D]" value={form.school_code || ""} onChange={e => setForm({ ...form, school_code: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Number</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.route_number || ""} onChange={e => setForm({ ...form, route_number: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Mandal</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.mandal || ""} onChange={e => setForm({ ...form, mandal: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">District</label>
            <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.district || ""} onChange={e => setForm({ ...form, district: e.target.value })} /></div>
          <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Area</label>
            <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.area || ""} onChange={e => setForm({ ...form, area: e.target.value })}>
              <option value="">Select Area</option>
              {["HYD", "MEDCHAL", "RR"].map(a => <option key={a} value={a}>{a}</option>)}
            </select></div>
        </div>
      </div>
    );
    if (modal.type === "vehicle") return (
      <div className="space-y-4">
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number *</label>
          <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number || ""} onChange={e => setForm({ ...form, vehicle_number: e.target.value })} /></div>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Type</label>
          <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_type || "auto"} onChange={e => setForm({ ...form, vehicle_type: e.target.value })}>
            {["auto", "tempo", "van", "truck", "motorcycle", "other"].map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
          <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value })}>
            <option value="">Select Kitchen</option>
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select></div>
      </div>
    );
    if (modal.type === "coordinator") return (
      <div className="space-y-4">
        {/* User selection: admins choose any user; non-admins see their own details locked */}
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">User *</label>
          {isAdmin ? (
            <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.user_id || ""} onChange={e => setForm({ ...form, user_id: e.target.value })}>
              <option value="">Select User</option>
              {users
                .filter(u =>
                  !u.is_admin &&
                  !["Super Admin", "Administration", "Semi- Admin"].includes(u.role_name || "") &&
                  (u.role_name || "").toLowerCase() !== "manager" &&
                  (u.role_name || "").toLowerCase() !== "sr. manager" &&
                  (u.role_name || "").toLowerCase() !== "general manager" &&
                  (u.role_name || "").toLowerCase() !== "asst. manager"
                )
                .map(u => <option key={u.id} value={u.id}>{u.name} ({u.email || u.phone || "—"})</option>)
              }
            </select>
          ) : (
            <div className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm bg-[#F4F3F0] text-[#5C5147] flex items-center justify-between">
              <span className="font-medium">{currentUser?.name} ({currentUser?.email})</span>
              <span className="text-xs text-[#8A7E73] bg-white border border-[#E5E1DB] px-2 py-0.5 rounded-full">You</span>
            </div>
          )}
        </div>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
          <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value })}>
            <option value="">Select Kitchen</option>
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select></div>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Routes (select multiple)</label>
          <div className="max-h-40 overflow-y-auto border border-[#E5E1DB] rounded-lg p-2 space-y-1">
            {routes.map(r => (
              <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox"
                  checked={(form.route_ids || []).includes(r.id)}
                  onChange={e => {
                    const ids = form.route_ids || [];
                    setForm({ ...form, route_ids: e.target.checked ? [...ids, r.id] : ids.filter(x => x !== r.id) });
                  }} />
                Route {r.route_number}
              </label>
            ))}
          </div></div>
      </div>
    );
    if (modal.type === "assign") return (
      <div className="space-y-4">
        <p className="text-sm text-[#5C5147]">Route: <strong>{modal.route?.route_number}</strong></p>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle</label>
          <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_id || ""} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}>
            <option value="">Select Vehicle</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number} ({v.vehicle_type})</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_temp || false} onChange={e => setForm({ ...form, is_temp: e.target.checked })} />
          Temporary assignment
        </label>
      </div>
    );
    return null;
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="logistics-management">
      <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Logistics Management</h1>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {tabs.filter(t => !t.subKey || currentUser?.is_admin || currentUser?.permissions?.[`sub_${t.subKey}_view`] !== false).map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors -mb-px whitespace-nowrap ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {/* ─── ROUTE MASTER ─── */}
      {activeTab === "route_master" && (
        <TabErrorBoundary key="route_master">
          <RouteMasterTab routes={routes} kitchens={kitchens} currentUser={currentUser} onRefresh={fetchAll} />
        </TabErrorBoundary>
      )}

      {/* ─── ROUTES & VEHICLES ─── */}
      {activeTab === "routes" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <h2 className="font-bold text-[#3A322C]">Routes ({routes.length})</h2>
            <div className="flex gap-2 flex-wrap items-center">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                <input placeholder="Search routes..." className="border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-[#E8956D] w-44" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button onClick={() => exportCSV(filteredRoutes.map(r => ({ route_number: r.route_number, coordinator: r.coordinator_name, driver: r.driver_name, vehicle: r.driver_vehicle_number, contact: r.driver_contact, kitchen: getKitchenName(r.kitchen_id) })), "routes")} className="flex items-center gap-1 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={13} /> Export</button>
              <button onClick={() => openCreate("vehicle")} className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1">
                <Plus size={14} /><Truck size={14} /> Vehicle
              </button>
              <button onClick={() => openCreate("route")} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1">
                <Plus size={14} /> Route
              </button>
            </div>
          </div>

          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["Route No.", "Coordinator", "Driver Name", "Vehicle Number", "Driver Contact", "Kitchen", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {filteredRoutes.map(r => (
                    <tr key={r.id} className="hover:bg-[#FAFAF8]">
                      <td className="px-4 py-3">
                        <button onClick={() => openDetail("route", r)} className="font-bold text-[#E8956D] hover:underline">
                          {r.route_number}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[#8A7E73] font-medium">
                        {r.coordinator_name ? (
                          <button
                            onClick={() => {
                              const coord = coordinators.find(c => c.coordinator_name === r.coordinator_name);
                              if (coord) openDetail("coordinator", coord);
                            }}
                            className="hover:text-[#E8956D] hover:underline transition-colors"
                          >{r.coordinator_name}</button>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 font-medium text-[#3A322C]">{r.driver_name || "—"}</td>
                      <td className="px-4 py-3">
                        {r.driver_vehicle_number ? (
                          <span className="font-mono text-xs bg-[#F4F3F0] px-2 py-0.5 rounded">{r.driver_vehicle_number}</span>
                        ) : r.assigned_vehicle_id ? (
                          <span className="text-[#637C64] font-medium">{getVehicleNum(r.assigned_vehicle_id)}</span>
                        ) : <span className="text-[#C4BAAF]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-[#5C5147]">{r.driver_contact || "—"}</td>
                      <td className="px-4 py-3 text-[#8A7E73]">{getKitchenName(r.kitchen_id)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openAssign(r)} className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-1 rounded font-bold hover:bg-[#E8956D]/20">Assign</button>
                          <button onClick={() => openEdit("route", r)} className="text-[#8A7E73] hover:text-[#E8956D]" title="Edit route name"><Edit size={15} /></button>
                          {isAdmin && <button onClick={() => handleDelete("route", r.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="font-bold text-[#3A322C] mb-3">Vehicles ({vehicles.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {filteredVehicles.map(v => (
                <div key={v.id} className="bg-white border border-[#E5E1DB] rounded-xl p-3 text-center">
                  <Truck size={20} className="mx-auto mb-2 text-[#8A7E73]" />
                  <p className="font-bold text-xs text-[#3A322C]">{v.vehicle_number}</p>
                  <p className="text-[10px] text-[#8A7E73] capitalize">{v.vehicle_type}</p>
                  <div className="flex gap-1 justify-center mt-2">
                    <button onClick={() => openEdit("vehicle", v)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={12} /></button>
                    <button onClick={() => handleDelete("vehicle", v.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ─── ROUTE COORDINATORS ─── */}
      {activeTab === "coordinators" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-bold text-[#3A322C]">Route Coordinators ({coordinators.length})</h2>
            <button onClick={() => openCreate("coordinator")} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1">
              <Plus size={14} /> Add Coordinator
            </button>
          </div>
          <p className="text-xs text-[#8A7E73]">Click a coordinator name to see full details. Click route numbers to see route info.</p>
          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["Coordinator Name", "Assigned Routes", "Total", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {filteredCoords.map(c => {
                    const routeNums = c.route_numbers?.length > 0
                      ? c.route_numbers
                      : (c.route_ids || []).map(rid => routes.find(x => x.id === rid)?.route_number).filter(Boolean);
                    const isExpanded = !!expandedRows[c.id];
                    const visibleRoutes = isExpanded ? routeNums : routeNums.slice(0, 8);
                    const hiddenCount = routeNums.length - 8;
                    return (
                      <tr key={c.id} className="hover:bg-[#FAFAF8]">
                        {/* Clickable coordinator name */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => openDetail("coordinator", c)}
                            className="text-left group"
                            data-testid={`coordinator-detail-${c.id}`}
                          >
                            <p className="font-bold text-[#3A322C] group-hover:text-[#E8956D] transition-colors flex items-center gap-1">
                              {c.coordinator_name || c.user_name}
                              <ChevronRight size={13} className="text-[#C4BAAF] group-hover:text-[#E8956D]" />
                            </p>
                            {c.user_email && <p className="text-xs text-[#8A7E73]">{c.user_email}</p>}
                          </button>
                        </td>
                        {/* Expandable route pills */}
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 items-center">
                            {visibleRoutes.map((rnum, i) => {
                              const routeObj = routes.find(r => r.route_number === rnum || String(r.route_number) === String(rnum));
                              return (
                                <button
                                  key={`idx-${i}`}
                                  onClick={() => routeObj && openDetail("route", routeObj)}
                                  className="text-xs font-bold bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full hover:bg-[#E8956D]/25 transition-colors"
                                  title={routeObj ? `Route ${rnum} — Driver: ${routeObj.driver_name || "—"}, Vehicle: ${routeObj.driver_vehicle_number || "—"}` : `Route ${rnum}`}
                                >
                                  {rnum}
                                </button>
                              );
                            })}
                            {!isExpanded && hiddenCount > 0 && (
                              <button
                                onClick={() => toggleExpand(c.id)}
                                className="text-xs font-bold text-[#4285F4] bg-blue-50 px-2 py-0.5 rounded-full hover:bg-blue-100 transition-colors"
                                data-testid={`expand-routes-${c.id}`}
                              >
                                +{hiddenCount} more
                              </button>
                            )}
                            {isExpanded && routeNums.length > 8 && (
                              <button
                                onClick={() => toggleExpand(c.id)}
                                className="text-xs text-[#8A7E73] hover:text-[#5C5147] px-1"
                              >
                                show less
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-bold text-[#3A322C]">{routeNums.length}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => openEdit("coordinator", c)} className="text-[#8A7E73] hover:text-[#E8956D]" title="Edit coordinator"><Edit size={15} /></button>
                            {isAdmin && (
                              <button onClick={() => handleDelete("coordinator", c.id)} className="text-[#8A7E73] hover:text-[#C2624E]" title="Delete (admin only)"><Trash2 size={15} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {coordinators.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-10 text-[#8A7E73]">No coordinators found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── SCHOOL MASTER ─── */}
      {activeTab === "schools" && (
        <div className="space-y-4">
          {/* Stats cards */}
          {schoolStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Total Schools</p>
                <p className="text-2xl font-extrabold text-[#E8956D]">{schoolStats.total}</p>
              </div>
              {(schoolStats.by_area || []).map(a => (
                <div key={a.area} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">{a.area} Area</p>
                  <p className="text-2xl font-extrabold text-[#637C64]">{a.count}</p>
                </div>
              ))}
            </div>
          )}

          {/* Upload School Data — admin only */}
          {isAdmin && (
            <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-[#3A322C] text-sm">Upload School Data</h3>
                  <p className="text-xs text-[#8A7E73] mt-0.5">
                    Upload a CSV or Excel file to add new school records. Columns: school_name, school_code, mandal, district, area, coordinator_name, route_number.
                  </p>
                </div>
                <a
                  href={logisticsAPI.downloadSchoolMasterTemplate()}
                  className="flex items-center gap-1 text-xs border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0] whitespace-nowrap"
                  download
                >
                  <Download size={12} /> Template
                </a>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <input
                  ref={schoolUploadRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  id="school-master-upload"
                  onChange={e => handleSchoolUpload(e.target.files[0])}
                  data-testid="school-master-file-input"
                />
                <label
                  htmlFor="school-master-upload"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-colors ${schoolUploadLoading ? "bg-[#F4F3F0] text-[#8A7E73] cursor-not-allowed" : "bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661]"}`}
                  data-testid="school-master-upload-btn"
                >
                  {schoolUploadLoading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                  {schoolUploadLoading ? "Uploading…" : "Upload CSV / Excel"}
                </label>

                {schoolUploadResult && (
                  <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${schoolUploadResult.success ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                    {schoolUploadResult.success
                      ? <><CheckCircle size={14} /> {schoolUploadResult.message}</>
                      : <><AlertTriangle size={14} /> {schoolUploadResult.error}</>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-[#3A322C] text-sm">Filter Schools</h3>
              <div className="flex gap-2">
                <button onClick={exportSchools} className="flex items-center gap-1 text-sm border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={13} /> Export CSV
                </button>
                <button onClick={() => fetchSchools()} className="flex items-center gap-1 text-sm bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-1.5 font-bold">
                  <Search size={13} /> Search
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="col-span-2 relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                <input
                  placeholder="Search school name, mandal..."
                  className="w-full border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={schoolSearch}
                  onChange={e => setSchoolSearch(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && fetchSchools()}
                  data-testid="school-search"
                />
              </div>
              <div>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={schoolFilter.coordinator}
                  onChange={e => setSchoolFilter(f => ({ ...f, coordinator: e.target.value }))}
                  data-testid="school-coordinator-filter">
                  <option value="">All Coordinators</option>
                  {COORDINATORS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <input placeholder="Route Number" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={schoolFilter.route_number}
                  onChange={e => setSchoolFilter(f => ({ ...f, route_number: e.target.value }))}
                  data-testid="school-route-filter" />
              </div>
              <div>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={schoolFilter.area}
                  onChange={e => setSchoolFilter(f => ({ ...f, area: e.target.value }))}>
                  <option value="">All Areas</option>
                  <option value="HYD">HYD</option>
                  <option value="MEDCHAL">MEDCHAL</option>
                  <option value="RR">RR</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
              <p className="text-sm font-bold text-[#3A322C]">Showing {schools.length} of {schoolTotal} schools</p>
              {schoolLoading && <RefreshCw size={14} className="animate-spin text-[#E8956D]" />}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["Route No.", "School Code", "Mandal", "School Name", "Area", "Coords", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {schools.map((s, i) => (
                    <tr key={`item-${i}`} className="hover:bg-[#FAFAF8]" data-testid={`school-row-${i}`}>
                      <td className="px-4 py-3 font-bold text-[#E8956D]">{s.route_number}</td>
                      <td className="px-4 py-3">
                        {s.school_code
                          ? <span className="font-mono text-xs bg-[#F4F3F0] px-2 py-0.5 rounded font-bold text-[#5C5147]">{s.school_code}</span>
                          : <span className="text-xs text-[#C4BAAF] italic">Pending review</span>}
                      </td>
                      <td className="px-4 py-3 text-[#5C5147] font-medium">{s.mandal}</td>
                      <td className="px-4 py-3 text-[#3A322C]">{s.school_name}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.area === "HYD" ? "bg-blue-100 text-blue-700" : s.area === "MEDCHAL" ? "bg-green-100 text-green-700" : "bg-purple-100 text-purple-700"}`}>
                          {s.area}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {s.lat && s.lng
                          ? <span className="flex items-center gap-1 text-xs text-[#637C64] font-bold"><Navigation size={12}/> Pinned</span>
                          : <span className="text-xs text-[#C4BAAF]">No pin</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 items-center">
                          {isAdmin && (
                            <button onClick={() => openMapPin(s)}
                              className="flex items-center gap-1 text-xs bg-[#E8956D]/10 text-[#E8956D] hover:bg-[#E8956D]/20 px-2 py-1 rounded font-bold"
                              title="Set map coordinates" data-testid={`pin-school-${i}`}>
                              <MapPin size={12}/> Pin
                            </button>
                          )}
                          <button onClick={() => openEdit("school", s)}
                            className="text-[#8A7E73] hover:text-[#E8956D]" title="Edit school">
                            <Edit size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {schools.length === 0 && !schoolLoading && (
                    <tr><td colSpan={6} className="text-center py-12 text-[#8A7E73]">
                      {schoolTotal === 0 ? "No school data found. Upload fresh data using the button above." : "No results. Try clearing filters."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── MAP PIN MODAL ─── */}
      {mapPinModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <div>
                <h2 className="font-bold text-lg text-[#3A322C]">Set Map Coordinates</h2>
                <p className="text-sm text-[#8A7E73] mt-0.5 max-w-md truncate">{mapPinModal.school_name}</p>
              </div>
              <button onClick={() => setMapPinModal(null)} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20}/></button>
            </div>
            <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
              <p className="text-sm text-amber-800 font-medium">
                <strong>Click anywhere on the map</strong> to drop a pin, or drag the existing pin to move it.
              </p>
              {pendingCoords && (
                <p className="text-xs text-amber-700 mt-1 font-mono">
                  Selected: {pendingCoords.lat.toFixed(6)}, {pendingCoords.lng.toFixed(6)}
                </p>
              )}
            </div>
            <div ref={mapContainerRef} style={{ height: "380px", width: "100%" }} data-testid="school-map-pin"/>
            {mapPinMsg && (
              <div className={`mx-6 my-3 px-4 py-2 rounded-xl text-sm font-bold ${mapPinMsg.startsWith("Error") ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                {mapPinMsg}
              </div>
            )}
            <div className="px-6 py-4 border-t border-[#E5E1DB] flex gap-3">
              <button onClick={() => setMapPinModal(null)} className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button
                onClick={saveMapPin}
                disabled={!pendingCoords || mapPinLoading}
                className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="save-map-pin-btn">
                {mapPinLoading ? <RefreshCw size={14} className="animate-spin"/> : <MapPin size={14}/>}
                {mapPinLoading ? "Saving…" : pendingCoords ? "Save Pin Location" : "Click map to place pin"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* ─── SCHOOL ASSIGNMENT ─── */}
      {activeTab === "school_assignment" && (
        <TabErrorBoundary key="school_assignment">
          <SchoolAssignmentTab routes={routes} currentUser={currentUser} onRefresh={fetchAll} />
        </TabErrorBoundary>
      )}

      {/* ─── VEHICLES & DRIVERS ─── */}
      {activeTab === "vehicles_drivers" && (
        <TabErrorBoundary key="vehicles_drivers">
          <VehiclesDriversTab currentUser={currentUser} />
        </TabErrorBoundary>
      )}

      {/* ─── RECONCILIATION ─── */}
      {activeTab === "reconciliation" && (
        <TabErrorBoundary key="reconciliation">
          <ReconciliationTab currentUser={currentUser} />
        </TabErrorBoundary>
      )}

      {/* ─── TEMP ASSIGNMENTS ─── */}
      {activeTab === "temp" && (
        <TabErrorBoundary key="temp">
          <TempAssignmentsTab
            routes={routes}
            coordinators={coordinators}
            currentUser={currentUser}
          />
        </TabErrorBoundary>
      )}

      {/* ─── MAINTENANCE TAB ─── */}
      {activeTab === "maintenance" && (
        <TabErrorBoundary key="maintenance">
          <MaintenanceTab routes={routes} kitchens={kitchens} selectedKitchen={selectedKitchen} />
        </TabErrorBoundary>
      )}

      {/* ─── FUEL LOG TAB ─── */}
      {activeTab === "fuel" && (
        <TabErrorBoundary key="fuel">
          <FuelTab routes={routes} kitchens={kitchens} selectedKitchen={selectedKitchen} vehicles={vehicles} />
        </TabErrorBoundary>
      )}

      {/* ─── REPORTS ─── */}
      {activeTab === "reports" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <h2 className="font-bold text-[#3A322C]">Logistics Reports</h2>
            <button onClick={exportReport} disabled={reportData.length === 0} className="flex items-center gap-1 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-40">
              <Download size={13} /> Export CSV
            </button>
          </div>

          {/* Date filter */}
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">From Date</label>
                <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={reportFrom} onChange={e => setReportFrom(e.target.value)} data-testid="report-from-date" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">To Date</label>
                <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                  value={reportTo} onChange={e => setReportTo(e.target.value)} data-testid="report-to-date" />
              </div>
              <button onClick={fetchReport} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="run-report-btn">
                {reportLoading ? <RefreshCw size={14} className="animate-spin" /> : <BarChart2 size={14} />} Run Report
              </button>
            </div>
          </div>

          {/* Report table */}
          {reportData.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[#E5E1DB] rounded-xl text-[#8A7E73]">
              {reportLoading ? <RefreshCw size={20} className="animate-spin mx-auto" /> : "No temporary assignments in this date range. Add assignments in the Temp Assignments tab."}
            </div>
          ) : (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>{["Date", "Absent (Original)", "Covered By", "Routes", "Reason", "Recorded By"].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147] whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {reportData.map(r => (
                      <tr key={r.id} className="hover:bg-[#FAFAF8]">
                        <td className="px-4 py-3 font-bold text-[#3A322C]">{r.date}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">{r.original_coordinator}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">{r.replacement_coordinator}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(r.route_numbers || []).map(rn => (
                              <span key={rn} className="text-xs font-bold bg-[#E8956D]/10 text-[#E8956D] px-1.5 py-0.5 rounded">{rn}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#8A7E73] text-xs">{r.reason || "—"}</td>
                        <td className="px-4 py-3 text-[#8A7E73] text-xs">{r.created_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <Modal
          title={modal.type === "assign" ? "Assign Vehicle to Route" : `${modal.mode === "create" ? "Add" : "Edit"} ${modal.type}`}
          onClose={() => setModal(null)}
        >
          <ModalForm />
          {error && <p className="mt-4 text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 mt-6">
            <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">Cancel</button>
            <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] hover:bg-[#D48661]">Save</button>
          </div>
        </Modal>
      )}
      {ConfirmModal}

      {/* ─── DETAIL DRAWER ─── */}
      {detailDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="fixed inset-0 bg-black/30" onClick={() => setDetailDrawer(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl border-l border-[#E5E1DB] flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E1DB] bg-[#FAFAF8] sticky top-0">
              <div>
                {detailDrawer.type === "coordinator" ? (
                  <>
                    <p className="text-xs font-bold uppercase text-[#8A7E73] mb-0.5">Route Coordinator</p>
                    <h2 className="text-xl font-extrabold text-[#3A322C]">{detailDrawer.data.coordinator_name || detailDrawer.data.user_name}</h2>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-bold uppercase text-[#8A7E73] mb-0.5">Route Details</p>
                    <h2 className="text-xl font-extrabold text-[#E8956D]">Route {detailDrawer.data.route_number}</h2>
                  </>
                )}
              </div>
              <button onClick={() => setDetailDrawer(null)} className="text-[#8A7E73] hover:text-[#3A322C] p-2 rounded-lg hover:bg-[#F4F3F0]">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6 flex-1">
              {/* COORDINATOR DETAIL */}
              {detailDrawer.type === "coordinator" && (() => {
                const c = detailDrawer.data;
                const routeNums = c.route_numbers?.length > 0
                  ? c.route_numbers
                  : (c.route_ids || []).map(rid => routes.find(x => x.id === rid)?.route_number).filter(Boolean);

                const fullRouteObjs = routeNums.map(rnum => routes.find(r => r.route_number === rnum || String(r.route_number) === String(rnum))).filter(Boolean);

                // Find today's temp assignment
                const todayStr = new Date().toISOString().split("T")[0];
                const todayTemp = tempAssignments.find(a =>
                  a.date === todayStr && (a.original_coordinator === c.coordinator_name || a.replacement_coordinator === c.coordinator_name)
                );

                return (
                  <>
                    {/* Contact info */}
                    <div className="bg-[#F4F3F0] rounded-xl p-4 space-y-2">
                      {c.user_email && (
                        <div className="flex items-center gap-2 text-sm text-[#5C5147]">
                          <Mail size={14} className="text-[#8A7E73]" />
                          <span>{c.user_email}</span>
                        </div>
                      )}
                      {c.user_name && c.user_name !== c.coordinator_name && (
                        <div className="flex items-center gap-2 text-sm text-[#5C5147]">
                          <Info size={14} className="text-[#8A7E73]" />
                          <span>Linked user: {c.user_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-[#5C5147]">
                        <Map size={14} className="text-[#8A7E73]" />
                        <span>{routeNums.length} routes assigned</span>
                      </div>
                    </div>

                    {/* Today's status */}
                    {todayTemp && (
                      <div className={`rounded-xl p-4 border ${todayTemp.original_coordinator === c.coordinator_name ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                        <p className="text-xs font-bold uppercase text-[#8A7E73] mb-1">Today's Assignment</p>
                        {todayTemp.original_coordinator === c.coordinator_name ? (
                          <p className="text-sm font-bold text-red-700">On leave today — {todayTemp.replacement_coordinator} is covering</p>
                        ) : (
                          <p className="text-sm font-bold text-green-700">Covering for {todayTemp.original_coordinator} today</p>
                        )}
                        {todayTemp.reason && <p className="text-xs text-[#8A7E73] mt-1">Reason: {todayTemp.reason}</p>}
                      </div>
                    )}

                    {/* All assigned routes */}
                    <div>
                      <p className="text-xs font-bold uppercase text-[#8A7E73] mb-3">All Assigned Routes ({routeNums.length})</p>
                      {fullRouteObjs.length > 0 ? (
                        <div className="space-y-2">
                          {fullRouteObjs.map((r, i) => (
                            <div key={`item-${i}`} className="bg-white border border-[#E5E1DB] rounded-lg px-4 py-3 flex items-center justify-between hover:border-[#E8956D]/50 transition-colors">
                              <div>
                                <span className="font-bold text-[#E8956D] mr-3">Route {r.route_number}</span>
                                <span className="text-sm text-[#3A322C]">{r.driver_name || r.name || "—"}</span>
                              </div>
                              <div className="text-right">
                                {r.driver_vehicle_number && (
                                  <p className="font-mono text-xs bg-[#F4F3F0] px-2 py-0.5 rounded">{r.driver_vehicle_number}</p>
                                )}
                                {r.driver_contact && (
                                  <p className="text-xs text-[#8A7E73] mt-0.5 flex items-center gap-1 justify-end">
                                    <Phone size={10} /> {r.driver_contact}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                          {routeNums.length > fullRouteObjs.length && (
                            <p className="text-xs text-[#8A7E73] px-1">{routeNums.length - fullRouteObjs.length} route(s) not yet assigned to a driver</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {routeNums.map((rnum, i) => (
                            <span key={`item-${i}`} className="inline-block mr-1 mb-1 text-xs font-bold bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full">{rnum}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* ROUTE DETAIL */}
              {detailDrawer.type === "route" && (() => {
                const r = detailDrawer.data;
                const coord = coordinators.find(c => c.coordinator_name === r.coordinator_name);
                // Get schools for this route from school master (via coordinators state)
                return (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#F4F3F0] rounded-xl p-4">
                        <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Driver</p>
                        <p className="font-bold text-[#3A322C]">{r.driver_name || r.name || "—"}</p>
                      </div>
                      <div className="bg-[#F4F3F0] rounded-xl p-4">
                        <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Vehicle</p>
                        <p className="font-bold font-mono text-[#3A322C]">{r.driver_vehicle_number || "—"}</p>
                      </div>
                      <div className="bg-[#F4F3F0] rounded-xl p-4">
                        <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Coordinator</p>
                        <button
                          onClick={() => coord && setDetailDrawer({ type: "coordinator", data: coord })}
                          className={`font-bold ${coord ? "text-[#E8956D] hover:underline" : "text-[#3A322C]"}`}
                        >
                          {r.coordinator_name || "—"}
                        </button>
                      </div>
                      <div className="bg-[#F4F3F0] rounded-xl p-4">
                        <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Contact</p>
                        <p className="font-bold text-[#3A322C]">
                          {r.driver_contact ? (
                            <a href={`tel:${r.driver_contact}`} className="flex items-center gap-1 text-[#4285F4] hover:underline">
                              <Phone size={12} />{r.driver_contact}
                            </a>
                          ) : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="bg-[#F4F3F0] rounded-xl p-4">
                      <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Kitchen</p>
                      <p className="font-bold text-[#3A322C]">{getKitchenName(r.kitchen_id) || "—"}</p>
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">Actions</p>
                      <div className="flex gap-2">
                        <button onClick={() => { setDetailDrawer(null); openEdit("route", r); }} className="flex-1 border border-[#E5E1DB] rounded-lg py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center justify-center gap-1">
                          <Edit size={14} /> Edit Route
                        </button>
                        <button onClick={() => { setDetailDrawer(null); openAssign(r); }} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2 text-sm font-bold flex items-center justify-center gap-1">
                          <Truck size={14} /> Assign Vehicle
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#E5E1DB] p-4 sticky bottom-0 bg-white">
              <button onClick={() => setDetailDrawer(null)} className="w-full border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── SCHOOL ASSIGNMENT TAB ────────────────────────────────────────────────────
function SchoolAssignmentTab({ routes, currentUser, onRefresh }) {
  const canEdit = !!(currentUser?.is_admin ||
    currentUser?.permissions?.route_planner ||
    currentUser?.permissions?.route_assignment_edit);

  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  // Available schools state
  const [availSchools, setAvailSchools] = useState([]);
  const [availSearch, setAvailSearch] = useState("");
  const [availLoading, setAvailLoading] = useState(false);
  const [availTotal, setAvailTotal] = useState(0);
  const [checked, setChecked] = useState(new Set());

  // refreshKey increments after each add/remove to re-trigger the available-schools fetch
  const [refreshKey, setRefreshKey] = useState(0);

  // Action state
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [moveConfirm, setMoveConfirm] = useState(null); // { schoolsToMove: [], schoolsToAdd: [] }

  const PAGE = 50;

  const sortedRoutes = [...(routes || [])]
    .filter(r => !r.is_retired)
    .sort((a, b) => Number(a.route_number) - Number(b.route_number));

  const filteredCombo = search.trim()
    ? sortedRoutes.filter(r => String(r.route_number).includes(search.trim()))
    : sortedRoutes;

  const selected = sortedRoutes.find(r => r.id === selectedRouteId);

  const assignedSchools = selected
    ? [...(selected.stops || [])].sort(
        (a, b) => (a.sequence ?? 9999) - (b.sequence ?? 9999) ||
                  (a.school_name || "").localeCompare(b.school_name || "")
      )
    : [];
  const assignedIds = new Set(assignedSchools.map(s => s.school_id));

  // Fetch available schools
  useEffect(() => {
    if (!selectedRouteId) { setAvailSchools([]); setAvailTotal(0); return; }
    let cancelled = false;
    const run = async () => {
      setAvailLoading(true);
      try {
        const params = { limit: PAGE };
        if (availSearch.trim()) params.search = availSearch.trim();
        const r = await routePlannerAPI.getSchools(params);
        if (cancelled) return;
        const all = r.data?.schools || [];
        setAvailSchools(all.filter(s => !assignedIds.has(s.id)));
        setAvailTotal(r.data?.total ?? all.length);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('avail schools error:', e);
      }
      if (!cancelled) setAvailLoading(false);
    };
    const timer = setTimeout(run, availSearch ? 250 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedRouteId, availSearch, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on route change
  useEffect(() => { setChecked(new Set()); setAvailSearch(""); setActionMsg(""); setActionErr(""); }, [selectedRouteId]);

  useEffect(() => {
    const handler = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (route) => { setSelectedRouteId(route.id); setSearch(""); setOpen(false); };
  const toggleCheck = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const flashMsg = (m) => { setActionMsg(m); setTimeout(() => setActionMsg(""), 3500); };
  const flashErr = (m) => { setActionErr(m); setTimeout(() => setActionErr(""), 5000); };


  // ── ADD SELECTED: check for moves, confirm if needed ──
  const handleAddClick = () => {
    if (!selectedRouteId || checked.size === 0) return;
    const checkedList = availSchools.filter(s => checked.has(s.id));
    const schoolsToMove = checkedList.filter(s => s.route_assignment?.route_id);
    const schoolsToAdd  = checkedList.filter(s => !s.route_assignment?.route_id);

    if (schoolsToMove.length > 0) {
      // Need confirmation for moves
      setMoveConfirm({ schoolsToMove, schoolsToAdd, targetRoute: selected });
    } else {
      // Pure adds — no confirmation needed
      executeAdd(schoolsToAdd, []);
    }
  };

  const executeAdd = async (schoolsToAdd, schoolsToMove) => {
    if (!selectedRouteId) return;
    setSaving(true); setMoveConfirm(null);
    let added = 0, moved = 0, failed = 0;
    try {
      // 1. Move schools from their current routes
      for (const s of schoolsToMove) {
        try {
          const oldRouteId = s.route_assignment.route_id;
          await routePlannerAPI.removeSchool(oldRouteId, s.id);
          await routePlannerAPI.addSchool(selectedRouteId, { school_id: s.id });
          moved++;
        } catch (e) { failed++; if (process.env.NODE_ENV === 'development') console.error('move error:', e); }
      }
      // 2. Add schools that weren't on any route
      for (const s of schoolsToAdd) {
        try {
          await routePlannerAPI.addSchool(selectedRouteId, { school_id: s.id });
          added++;
        } catch (e) { failed++; if (process.env.NODE_ENV === 'development') console.error('add error:', e); }
      }
      const parts = [];
      if (added) parts.push(`${added} added`);
      if (moved) parts.push(`${moved} moved`);
      if (failed) parts.push(`${failed} failed`);
      flashMsg(parts.join(", ") + ` to Route ${selected?.route_number}`);
    } finally {
      setSaving(false);
      setChecked(new Set());
      // Re-fetch parent routes (updates assigned counts + stops in place, keeps route selected)
      if (onRefresh) await onRefresh();
      // Re-fetch available schools (filters out newly assigned ones)
      setRefreshKey(k => k + 1);
    }
  };

  // ── REMOVE school from assigned ──
  const handleRemove = async (schoolId, schoolName) => {
    if (!selectedRouteId) return;
    setSaving(true);
    try {
      await routePlannerAPI.removeSchool(selectedRouteId, schoolId);
      flashMsg(`${schoolName} removed from Route ${selected?.route_number}`);
      // Re-fetch in place — route stays selected
      if (onRefresh) await onRefresh();
      setRefreshKey(k => k + 1);
    } catch (e) {
      flashErr(e?.response?.data?.detail || "Remove failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="school-assignment-tab">
      {/* Status messages */}
      {actionMsg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-2.5 text-sm font-medium">{actionMsg}</div>}
      {actionErr && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-2.5 text-sm">{actionErr}</div>}

      {/* ── Route combobox (Step 1 — unchanged) ── */}
      <div className="max-w-sm">
        <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Select Route</label>
        <div ref={dropRef} className="relative">
          <div className="flex items-center gap-2 w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm cursor-text focus-within:border-[#E8956D] bg-white"
            onClick={() => setOpen(true)}>
            {!open && selected && !search && (
              <span className="flex-1 text-[#3A322C] font-medium">Route {selected.route_number}</span>
            )}
            <input value={search} onChange={e => { setSearch(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={selected && !open ? "" : "Search by route number…"}
              className="flex-1 outline-none bg-transparent text-sm placeholder:text-[#C4BAAF]"
              data-testid="route-search-input" />
            {(selected || search) && (
              <button onClick={e => { e.stopPropagation(); setSelectedRouteId(""); setSearch(""); setOpen(false); }}
                className="text-[#C4BAAF] hover:text-[#C2624E] shrink-0"><X size={14}/></button>
            )}
          </div>
          {open && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-[#E5E1DB] rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {filteredCombo.length === 0
                ? <p className="px-4 py-3 text-xs text-[#8A7E73]">No routes match "{search}"</p>
                : filteredCombo.map(r => (
                    <button key={r.id} onClick={() => pick(r)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F4F3F0] ${r.id === selectedRouteId ? "bg-[#E8956D]/10 text-[#E8956D] font-bold" : "text-[#3A322C]"}`}
                      data-testid={`route-option-${r.route_number}`}>
                      Route {r.route_number}
                    </button>
                  ))
              }
            </div>
          )}
        </div>
        <p className="text-xs text-[#C4BAAF] mt-1">{filteredCombo.length} of {sortedRoutes.length} routes shown</p>
      </div>

      <p className="text-sm text-[#5C5147]" data-testid="selected-route-text">
        {selected ? `Selected route: ${selected.route_number}` : "No route selected"}
      </p>

      {/* ── Two-column panels ── */}
      {selected && (
        <div className="flex gap-4 flex-wrap lg:flex-nowrap">

          {/* LEFT — Assigned Schools (Step 2 + remove button) */}
          <div className="flex-1 min-w-0 bg-white border border-[#E5E1DB] rounded-xl overflow-hidden" data-testid="assigned-schools-panel">
            <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
              <h3 className="font-bold text-[#3A322C] text-sm">
                Assigned Schools
                <span className="ml-2 text-[#E8956D]" data-testid="assigned-count">({assignedSchools.length})</span>
              </h3>
            </div>
            {assignedSchools.length === 0 ? (
              <p className="px-5 py-8 text-sm text-[#8A7E73] text-center">No schools assigned to this route.</p>
            ) : (
              <div className="overflow-y-auto max-h-[480px]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F8F6] sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#8A7E73] w-10">#</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#8A7E73]">School Name</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#8A7E73] w-28">UDISE</th>
                      {canEdit && <th className="w-8" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {assignedSchools.map((stop, i) => (
                      <tr key={stop.school_id || i} className="hover:bg-[#FAFAF8] group">
                        <td className="px-4 py-2.5 text-xs font-bold text-[#E8956D]">{stop.sequence ?? i + 1}</td>
                        <td className="px-4 py-2.5 font-medium text-[#3A322C] text-xs">{stop.school_name}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-[#8A7E73]">{stop.school_code || "—"}</td>
                        {canEdit && (
                          <td className="px-2 py-2.5">
                            <button
                              onClick={() => handleRemove(stop.school_id, stop.school_name)}
                              disabled={saving}
                              className="text-[#C4BAAF] hover:text-[#C2624E] opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                              title={`Remove ${stop.school_name} from Route ${selected.route_number}`}
                              data-testid={`remove-${stop.school_id}`}
                            ><X size={14}/></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* RIGHT — Available Schools (Step 3 + Add button) */}
          <div className="w-80 shrink-0 bg-white border border-[#E5E1DB] rounded-xl overflow-hidden" data-testid="available-schools-panel">
            <div className="px-4 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
              <h3 className="font-bold text-[#3A322C] text-sm">Available Schools</h3>
              {checked.size > 0 && (
                <span className="text-xs font-bold text-[#E8956D] bg-[#E8956D]/10 px-2 py-0.5 rounded-full"
                  data-testid="selected-count">{checked.size} selected</span>
              )}
            </div>
            <div className="px-3 py-2.5 border-b border-[#F4F3F0]">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#C4BAAF]"/>
                <input value={availSearch} onChange={e => setAvailSearch(e.target.value)}
                  placeholder="Name, UDISE code…"
                  className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-xs outline-none focus:border-[#E8956D]"
                  data-testid="available-search"/>
                {availSearch && (
                  <button onClick={() => setAvailSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#C4BAAF] hover:text-[#C2624E]"><X size={12}/></button>
                )}
              </div>
              <p className="text-[10px] text-[#C4BAAF] mt-1">
                {availLoading ? "Searching…" : `Showing ${availSchools.length}${availSearch ? "" : ` of ~${availTotal - assignedSchools.length} available`}`}
              </p>
            </div>

            <div className="overflow-y-auto max-h-[360px]" data-testid="available-list">
              {availLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-5 h-5 border-2 border-[#E8956D] border-t-transparent rounded-full animate-spin"/>
                </div>
              ) : availSchools.length === 0 ? (
                <p className="px-4 py-6 text-xs text-[#8A7E73] text-center">
                  {availSearch ? `No schools match "${availSearch}"` : "All schools are assigned to this route"}
                </p>
              ) : availSchools.map(s => (
                <label key={s.id}
                  className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer border-b border-[#F4F3F0] last:border-0 hover:bg-[#FAFAF8] ${checked.has(s.id) ? "bg-[#E8956D]/5" : ""}`}
                  data-testid={`avail-row-${s.id}`}>
                  {canEdit && (
                    <input type="checkbox" checked={checked.has(s.id)} onChange={() => toggleCheck(s.id)}
                      className="mt-0.5 shrink-0 accent-[#E8956D]" data-testid={`check-${s.id}`}/>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#3A322C] leading-snug">{s.school_name}</p>
                    {s.school_code && <p className="text-[10px] font-mono text-[#C4BAAF] mt-0.5">{s.school_code}</p>}
                    {s.route_assignment?.route_number && (
                      <p className="text-[10px] text-[#D98032] font-bold mt-0.5">
                        on Route {s.route_assignment.route_number}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Add Selected button */}
            {canEdit && (
              <div className="px-4 py-3 border-t border-[#F4F3F0]">
                <button
                  onClick={handleAddClick}
                  disabled={checked.size === 0 || saving}
                  className="w-full bg-[#E8956D] text-[#3A322C] rounded-lg py-2 text-xs font-bold hover:bg-[#D48661] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  data-testid="add-selected-btn">
                  {saving
                    ? <><span className="w-3 h-3 border-2 border-[#3A322C] border-t-transparent rounded-full animate-spin inline-block"/>{" "}Saving…</>
                    : checked.size > 0
                      ? `Add ${checked.size} to Route ${selected?.route_number}`
                      : "Select schools above"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Move confirmation modal ── */}
      {moveConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C] font-[Manrope]">Confirm School Moves</h2>
              <p className="text-xs text-[#8A7E73] mt-1">These schools will be moved from their current route to Route {moveConfirm.targetRoute?.route_number}.</p>
            </div>
            <div className="px-6 py-4 space-y-2 max-h-60 overflow-y-auto">
              {moveConfirm.schoolsToMove.map(s => (
                <div key={s.id} className="flex items-center gap-3 text-sm">
                  <div className="flex-1">
                    <span className="font-medium text-[#3A322C]">{s.school_name}</span>
                  </div>
                  <div className="text-xs text-[#D98032] font-bold whitespace-nowrap">
                    Route {s.route_assignment.route_number} → {moveConfirm.targetRoute?.route_number}
                  </div>
                </div>
              ))}
              {moveConfirm.schoolsToAdd.length > 0 && (
                <p className="text-xs text-[#8A7E73] pt-2 border-t border-[#F4F3F0]">
                  + {moveConfirm.schoolsToAdd.length} unassigned school(s) will also be added.
                </p>
              )}
            </div>
            <div className="px-6 py-4 flex gap-3">
              <button onClick={() => setMoveConfirm(null)}
                className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">
                Cancel
              </button>
              <button
                onClick={() => executeAdd(moveConfirm.schoolsToAdd, moveConfirm.schoolsToMove)}
                className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold hover:bg-[#D48661]"
                data-testid="confirm-move-btn">
                Confirm & Move
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── ROUTE MASTER TAB ─────────────────────────────────────────────────────────
function RouteMasterTab({ routes: routesProp, kitchens, currentUser, onRefresh }) {
  const canEdit = currentUser?.is_admin ||
    !!currentUser?.permissions?.route_assignment_edit ||
    !!currentUser?.permissions?.route_master_edit;
  const isAdmin = !!currentUser?.is_admin;

  const [allRoutes, setAllRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [clearing, setClearing] = useState(false);

  // Route Import state
  const [showImport, setShowImport] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const routeFileRef = useRef();

  // School Assignment Import state (from Route Master tab)
  const [showSchoolImport, setShowSchoolImport] = useState(false);
  const [schoolImportPreview, setSchoolImportPreview] = useState(null);
  const [schoolImportLoading, setSchoolImportLoading] = useState(false);
  const [schoolCommitting, setSchoolCommitting] = useState(false);
  const schoolFileRef = useRef();

  // Route Merge state
  const [showMerge, setShowMerge] = useState(false);
  const [mergeSurvivorId, setMergeSurvivorId] = useState("");
  const [mergeSourceIds, setMergeSourceIds] = useState(new Set());
  const [merging, setMerging] = useState(false);

  // Reset state
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await logisticsAPI.getRoutes({ include_retired: true });
      setAllRoutes(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const openCreate = () => { setForm({ route_number: "", name: "", kitchen_id: "", notes: "" }); setModal({ mode: "create" }); setErr(""); };
  const openEdit = (r) => { setForm({ ...r }); setModal({ mode: "edit", id: r.id }); setErr(""); };

  const handleSave = async () => {
    setErr("");
    if (!form.route_number?.toString().trim()) { setErr("Route number is required"); return; }
    try {
      if (modal.mode === "create") {
        await logisticsAPI.createRoute({ ...form, route_number: form.route_number.toString().trim() });
        flash("Route created!");
      } else {
        await logisticsAPI.updateRoute(modal.id, { kitchen_id: form.kitchen_id || "", notes: form.notes || "" });
        flash("Route updated!");
      }
      setModal(null); load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
  };

  // Route Import
  const handleImportPreview = async (file) => {
    setImportLoading(true); setErr(""); setImportPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await logisticsAPI.previewRouteImport(fd);
      setImportPreview(r.data);
    } catch (e) { setErr(formatError(e)); }
    setImportLoading(false);
  };

  const handleImportCommit = async () => {
    if (!importPreview) return;
    const rows = [...(importPreview.new_routes || []), ...(importPreview.updated_routes || [])];
    setCommitting(true); setErr("");
    try {
      const r = await logisticsAPI.commitRouteImport(rows);
      flash(r.data?.message || "Import complete!");
      setShowImport(false); setImportPreview(null);
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
    setCommitting(false);
  };

  // School Assignment Import
  const handleSchoolImportPreview = async (file) => {
    setSchoolImportLoading(true); setErr(""); setSchoolImportPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await logisticsAPI.previewSchoolImport(fd);
      setSchoolImportPreview(r.data);
    } catch (e) { setErr(formatError(e)); }
    setSchoolImportLoading(false);
  };

  const handleSchoolImportCommit = async () => {
    if (!schoolImportPreview?.clean?.length) return;
    setSchoolCommitting(true); setErr("");
    try {
      const r = await logisticsAPI.commitSchoolImport(schoolImportPreview.clean);
      flash(r.data?.message || "School assignments imported!");
      setShowSchoolImport(false); setSchoolImportPreview(null);
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
    setSchoolCommitting(false);
  };

  const downloadSchoolErrors = () => {
    if (!schoolImportPreview) return;
    const errors = [
      ...(schoolImportPreview.unmatched_school || []),
      ...(schoolImportPreview.unknown_route || []),
      ...(schoolImportPreview.ambiguous || []),
    ];
    const content = "row,school_identifier,identifier_type,route_number,reason\n" +
      errors.map(e => `${e.row},"${e.identifier}","${e.identifier_type}","${e.route_number}","${e.reason}"`).join("\n");
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "school_import_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Route Merge
  const handleMerge = async () => {
    if (!mergeSurvivorId || mergeSourceIds.size === 0) { setErr("Select survivor and at least one source route"); return; }
    const survivor = allRoutes.find(r => r.id === mergeSurvivorId);
    const sources = allRoutes.filter(r => mergeSourceIds.has(r.id));
    const totalSchools = sources.reduce((s, r) => s + (r.school_count || 0), 0);
    if (!window.confirm(
      `Merge ${sources.map(r => `Route ${r.route_number}`).join(", ")} into Route ${survivor?.route_number}?\n\n` +
      `${totalSchools} schools will move to Route ${survivor?.route_number}. ` +
      `Source routes will be retired. This is versioned and logged.`
    )) return;
    setMerging(true); setErr("");
    try {
      const r = await logisticsAPI.mergeRoutes({ survivor_id: mergeSurvivorId, source_ids: [...mergeSourceIds] });
      flash(r.data?.message || "Merge complete!");
      setShowMerge(false); setMergeSurvivorId(""); setMergeSourceIds(new Set());
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
    setMerging(false);
  };

  // Route Reset
  const handleReset = async () => {
    if (resetConfirm !== "RESET") { setErr("Type RESET to confirm"); return; }
    setResetting(true); setErr("");
    try {
      const r = await logisticsAPI.resetRouteData("RESET");
      flash(r.data?.message || "Reset complete!");
      setShowReset(false); setResetConfirm("");
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
    setResetting(false);
  };

  const handleRetire = async (r) => {
    if (!window.confirm(`Retire Route ${r.route_number}? It will be hidden from operations but historical data is preserved.`)) return;
    try {
      await logisticsAPI.retireRoute(r.id);
      flash(`Route ${r.route_number} retired`);
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
  };

  const handleUnretire = async (r) => {
    try {
      await logisticsAPI.unretireRoute(r.id);
      flash(`Route ${r.route_number} restored`);
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
  };

  const handleBulkClear = async () => {
    if (!window.confirm("Bulk-clear all legacy driver/person names embedded in routes?\n\nThis clears the 'driver_name' field from all routes. Driver information from the Vehicles & Drivers master is unaffected.")) return;
    setClearing(true);
    try {
      const r = await logisticsAPI.bulkClearRouteNames();
      flash(r.data?.message || "Legacy names cleared!");
      load(); onRefresh();
    } catch (e) { setErr(formatError(e)); }
    setClearing(false);
  };

  const displayed = allRoutes.filter(r => {
    if (!showRetired && r.is_retired) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return String(r.route_number).includes(q)  ||
      (r.coordinator_name || "").toLowerCase().includes(q);
  });

  const getKitchenName = (kid) => kitchens.find(k => k.id === kid)?.name || (kid ? "Kitchen" : "—");

  return (
    <div className="space-y-4" data-testid="route-master-tab">
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-2.5 text-sm flex items-center gap-2"><CheckCircle size={14}/>{msg}</div>}
      {err && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-2.5 text-sm">{err}<button onClick={() => setErr("")} className="ml-2 font-bold">×</button></div>}

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search routes..."
              className="border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D] w-48" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#5C5147] cursor-pointer">
            <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} className="accent-[#E8956D]" />
            Show Retired
          </label>
          <span className="text-xs text-[#8A7E73]">{displayed.length} routes</span>
        </div>
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleBulkClear} disabled={clearing}
              className="border border-[#D98032]/40 text-[#D98032] rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#FDF4EB] disabled:opacity-60 flex items-center gap-1.5">
              {clearing ? <Loader size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Clear Legacy Names
            </button>
            {/* Route Import */}
            <div className="flex gap-1">
              <a href={`${logisticsAPI.downloadRouteTemplate()}?token=${localStorage.getItem("manna_access_token")}`}
                className="flex items-center gap-1.5 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
                <Download size={13} /> Route Template
              </a>
              <button onClick={() => { setShowImport(true); setImportPreview(null); setErr(""); setShowSchoolImport(false); }}
                className="bg-[#637C64] text-white rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1.5 hover:bg-[#4F6450]"
                data-testid="import-routes-btn">
                <Upload size={13} /> Import Routes
              </button>
            </div>
            {/* School Assignment Import */}
            <div className="flex gap-1">
              <a href={`${logisticsAPI.downloadSchoolTemplate()}?token=${localStorage.getItem("manna_access_token")}`}
                className="flex items-center gap-1.5 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
                <Download size={13} /> School Template
              </a>
              <button onClick={() => { setShowSchoolImport(true); setSchoolImportPreview(null); setErr(""); setShowImport(false); }}
                className="bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1.5 hover:bg-blue-700"
                data-testid="import-school-assignments-btn">
                <Upload size={13} /> Import School Assignments
              </button>
            </div>
            <button onClick={() => { setShowMerge(!showMerge); setMergeSurvivorId(""); setMergeSourceIds(new Set()); setErr(""); }}
              className="border border-blue-300 text-blue-700 rounded-lg px-3 py-2 text-sm font-bold hover:bg-blue-50"
              data-testid="merge-routes-btn">
              Merge Routes
            </button>
            {isAdmin && (
              <button onClick={() => { setShowReset(!showReset); setResetConfirm(""); setErr(""); }}
                className="border border-[#C2624E]/40 text-[#C2624E] rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#FBEAE7]"
                data-testid="reset-route-data-btn">
                Reset Data
              </button>
            )}
            <button onClick={openCreate}
              className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5 hover:bg-[#D48661]"
              data-testid="new-route-btn">
              <Plus size={14} /> New Route
            </button>
          </div>
        )}
      </div>

      {/* Import Panel */}
      {showImport && (
        <div className="bg-white border border-[#637C64]/30 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#3A322C]">Route Import</h3>
            <button onClick={() => { setShowImport(false); setImportPreview(null); }}><X size={16} /></button>
          </div>
          <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#5C5147]">
            CSV columns: <span className="font-mono">route_number, kitchen, status</span> · Download template above
          </div>
          <div className="border-2 border-dashed border-[#E5E1DB] rounded-xl p-5 text-center cursor-pointer hover:border-[#637C64]/50" onClick={() => routeFileRef.current?.click()}>
            <Upload size={20} className="mx-auto mb-1 text-[#C4BAAF]" />
            <p className="text-sm font-bold text-[#5C5147]">{importPreview ? "File loaded — see preview below" : "Click to upload route CSV"}</p>
            <input ref={routeFileRef} type="file" accept=".csv" className="hidden" onChange={e => e.target.files[0] && handleImportPreview(e.target.files[0])} />
          </div>
          {importLoading && <div className="flex items-center gap-2 text-sm text-[#8A7E73]"><Loader size={13} className="animate-spin text-[#E8956D]" />Validating...</div>}
          {importPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                {[["Will Create", importPreview.summary?.will_create, "bg-[#EDF2EE] text-[#637C64]"],
                  ["Will Update", importPreview.summary?.will_update, "bg-blue-100 text-blue-700"],
                  ["Invalid", importPreview.summary?.invalid, "bg-[#FBEAE7] text-[#C2624E]"]].map(([l, v, c]) => (
                  <div key={l} className={`${c} rounded-xl p-3 text-center`}>
                    <p className="text-xs font-bold uppercase mb-0.5">{l}</p>
                    <p className="text-xl font-extrabold">{v}</p>
                  </div>
                ))}
              </div>
              {importPreview.invalid_rows?.length > 0 && (
                <div className="bg-[#FBEAE7] rounded-xl px-4 py-3 text-xs text-[#C2624E] space-y-1">
                  {importPreview.invalid_rows.slice(0,5).map((r, i) => <p key={`k-${i}`}>Row {r.row}: {r.reason}</p>)}
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button onClick={() => setImportPreview(null)} className="border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147]">Re-upload</button>
                <button onClick={handleImportCommit} disabled={committing || (importPreview.summary?.will_create + importPreview.summary?.will_update === 0)}
                  className="bg-[#637C64] text-white rounded-lg px-5 py-2 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
                  {committing ? <Loader size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  {committing ? "Importing..." : `Import ${importPreview.summary?.will_create + importPreview.summary?.will_update} Routes`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* School Assignment Import Panel */}
      {showSchoolImport && (
        <div className="bg-white border border-blue-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-[#3A322C]">Import School Assignments</h3>
              <p className="text-xs text-[#8A7E73] mt-0.5">Assign schools to routes in bulk via CSV. Routes must already exist in Route Master.</p>
            </div>
            <button onClick={() => { setShowSchoolImport(false); setSchoolImportPreview(null); }}><X size={16} /></button>
          </div>
          <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#5C5147]">
            CSV columns: <span className="font-mono">school_identifier, identifier_type (udise_code|official_name|aka_name), route_number, stop_order (optional)</span>
          </div>
          <input ref={schoolFileRef} type="file" accept=".csv" className="hidden" onChange={e => e.target.files[0] && handleSchoolImportPreview(e.target.files[0])} />
          <div className="border-2 border-dashed border-[#E5E1DB] rounded-xl p-5 text-center cursor-pointer hover:border-blue-400" onClick={() => schoolFileRef.current?.click()}>
            <Upload size={20} className="mx-auto mb-1 text-[#C4BAAF]" />
            <p className="text-sm font-bold text-[#5C5147]">{schoolImportPreview ? "File loaded — see preview below" : "Click to upload school assignment CSV"}</p>
          </div>
          {schoolImportLoading && <div className="flex items-center gap-2 text-sm text-[#8A7E73]"><Loader size={13} className="animate-spin text-blue-500" />Validating...</div>}
          {schoolImportPreview && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["Will Assign", (schoolImportPreview.clean||[]).length, "bg-[#EDF2EE] text-[#637C64]"],
                  ["No Change", (schoolImportPreview.already_same_route||[]).length, "bg-[#F4F3F0] text-[#8A7E73]"],
                  ["Errors", schoolImportPreview.summary?.errors || 0, "bg-[#FBEAE7] text-[#C2624E]"],
                  ["Total", schoolImportPreview.summary?.total_rows || 0, "bg-[#F4F3F0] text-[#3A322C]"],
                ].map(([l, v, c]) => (
                  <div key={l} className={`${c} rounded-xl p-3 text-center`}>
                    <p className="text-xs font-bold uppercase mb-0.5">{l}</p>
                    <p className="text-xl font-extrabold">{v}</p>
                  </div>
                ))}
              </div>
              {/* Will-assign preview */}
              {(schoolImportPreview.clean||[]).length > 0 && (
                <div className="bg-white border border-[#637C64]/30 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  <div className="px-4 py-2 bg-[#EDF2EE] border-b border-[#637C64]/20 text-xs font-bold text-[#637C64]">Will Assign ({(schoolImportPreview.clean||[]).length})</div>
                  {(schoolImportPreview.clean||[]).slice(0,10).map((r, i) => (
                    <div key={`k-${i}`} className="flex items-center gap-3 px-4 py-2 text-xs border-b border-[#F4F3F0] last:border-0">
                      <span className="flex-1 font-medium text-[#3A322C] truncate">{r.school_name}</span>
                      <span className="text-[#8A7E73]">{r.current_route || "—"}</span>
                      <span className="text-[#637C64] font-bold">→ Route {r.new_route}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Errors */}
              {schoolImportPreview.summary?.errors > 0 && (
                <div className="bg-[#FBEAE7] rounded-xl px-4 py-3 text-xs text-[#C2624E] space-y-1">
                  {[...(schoolImportPreview.unmatched_school||[]), ...(schoolImportPreview.unknown_route||[]), ...(schoolImportPreview.ambiguous||[])].slice(0,5).map((e,i) => (
                    <p key={`k-${i}`}>Row {e.row}: {e.reason}</p>
                  ))}
                  <button onClick={downloadSchoolErrors} className="mt-1 flex items-center gap-1 font-bold hover:underline"><Download size={11}/> Download Error CSV</button>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <button onClick={() => setSchoolImportPreview(null)} className="border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147]">Re-upload</button>
                <button onClick={handleSchoolImportCommit} disabled={schoolCommitting || !(schoolImportPreview.clean||[]).length}
                  className="bg-blue-600 text-white rounded-lg px-5 py-2 text-sm font-bold disabled:opacity-50 flex items-center gap-2"
                  data-testid="confirm-school-import-btn">
                  {schoolCommitting ? <Loader size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  {schoolCommitting ? "Importing..." : `Assign ${(schoolImportPreview.clean||[]).length} Schools`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Merge Panel */}
      {showMerge && (
        <div className="bg-white border border-blue-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#3A322C]">Merge Routes</h3>
            <button onClick={() => setShowMerge(false)}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Survivor Route (keep this)</label>
              <select value={mergeSurvivorId} onChange={e => setMergeSurvivorId(e.target.value)}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400">
                <option value="">— Select survivor —</option>
                {displayed.filter(r => !r.is_retired).map(r => (
                  <option key={r.id} value={r.id}>Route {r.route_number} ({r.school_count} schools)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Routes to Merge In (select multiple)</label>
              <div className="border border-[#E5E1DB] rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                {displayed.filter(r => !r.is_retired && r.id !== mergeSurvivorId).map(r => (
                  <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#FAFAF8] px-1 py-0.5 rounded">
                    <input type="checkbox" className="accent-blue-600"
                      checked={mergeSourceIds.has(r.id)}
                      onChange={e => { const n = new Set(mergeSourceIds); e.target.checked ? n.add(r.id) : n.delete(r.id); setMergeSourceIds(n); }} />
                    <span className="font-bold text-[#E8956D]">R{r.route_number}</span>
                    
                    <span className="ml-auto text-[#C4BAAF]">{r.school_count} schools</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {mergeSurvivorId && mergeSourceIds.size > 0 && (
            <div className="bg-blue-50 rounded-xl px-4 py-3 text-xs text-blue-700">
              {mergeSourceIds.size} route(s) will be merged into Route {allRoutes.find(r => r.id === mergeSurvivorId)?.route_number}. 
              All schools and assignments move to the survivor. Source routes are retired (not deleted).
            </div>
          )}
          {err && <p className="text-sm text-[#C2624E]">{err}</p>}
          <div className="flex justify-end">
            <button onClick={handleMerge} disabled={merging || !mergeSurvivorId || mergeSourceIds.size === 0}
              className="bg-blue-600 text-white rounded-lg px-5 py-2 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
              {merging ? <Loader size={13} className="animate-spin" /> : null}
              Merge {mergeSourceIds.size} Route(s)
            </button>
          </div>
        </div>
      )}

      {/* Reset Panel */}
      {showReset && isAdmin && (
        <div className="bg-[#FBEAE7] border border-[#C2624E]/20 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#C2624E]">Reset Route Data</h3>
            <button onClick={() => setShowReset(false)}><X size={16} /></button>
          </div>
          <div className="text-sm text-[#5C5147] space-y-2">
            <p><strong>What this does:</strong> Freezes all current route versions (with effective_to = now), then clears active stop assignments for a clean re-import via CSV.</p>
            <p><strong>What is preserved:</strong> All historical records, transactional data, route version snapshots. Nothing is hard-deleted.</p>
            <p><strong>Prerequisite:</strong> A verified backup must exist.</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-[#C2624E] mb-1 block">Type RESET to confirm</label>
            <input value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
              className="w-full border border-[#C2624E]/30 rounded-lg px-3 py-2.5 text-sm font-mono outline-none"
              placeholder="RESET" />
          </div>
          {err && <p className="text-sm text-[#C2624E]">{err}</p>}
          <button onClick={handleReset} disabled={resetting || resetConfirm !== "RESET"}
            className="bg-[#C2624E] text-white rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
            {resetting ? <Loader size={13} className="animate-spin" /> : null}
            {resetting ? "Resetting..." : "Execute Reset"}
          </button>
        </div>
      )}

      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="w-7 h-7 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Route No.", "Kitchen", "Coordinator(s)", "Vehicle", "Schools", "Status", canEdit ? "Actions" : ""].filter(Boolean).map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {displayed.map(r => (
                  <tr key={r.id} className={`hover:bg-[#FAFAF8] ${r.is_retired ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 font-bold text-[#E8956D] text-base">{r.route_number}</td>
                    <td className="px-4 py-3 text-[#8A7E73] text-xs">{getKitchenName(r.kitchen_id)}</td>
                    <td className="px-4 py-3 text-xs text-[#5C5147]">
                      {(r.assigned_coordinators || []).length > 0
                        ? r.assigned_coordinators.slice(0, 2).join(", ") + (r.assigned_coordinators.length > 2 ? ` +${r.assigned_coordinators.length - 2}` : "")
                        : <span className="text-[#C4BAAF]">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#5C5147]">{r.assigned_vehicle || r.driver_vehicle_number || <span className="text-[#C4BAAF]">—</span>}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-[#E8956D]">{r.school_count ?? 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.is_retired ? "bg-[#FBEAE7] text-[#C2624E]" : "bg-[#EDF2EE] text-[#637C64]"}`}>
                        {r.is_retired ? "Retired" : "Active"}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button onClick={() => openEdit(r)} className="text-[#8A7E73] hover:text-[#E8956D]" title="Edit"><Edit size={14} /></button>
                          {r.is_retired ? (
                            <button onClick={() => handleUnretire(r)} className="text-xs bg-[#EDF2EE] text-[#637C64] px-2 py-0.5 rounded font-bold hover:bg-[#637C64]/20">Restore</button>
                          ) : (
                            <button onClick={() => handleRetire(r)} className="text-[#8A7E73] hover:text-[#D98032]" title="Retire route"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {displayed.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8A7E73]">No routes found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C] font-[Manrope]">{modal.mode === "create" ? "Create New Route" : `Edit Route ${form.route_number}`}</h2>
              <button onClick={() => setModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {modal.mode === "create" && (
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Number *</label>
                  <input type="text" value={form.route_number || ""} onChange={e => setForm(f => ({ ...f, route_number: e.target.value }))}
                    placeholder="e.g. 999" className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#E8956D]" />
                </div>
              )}

              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
                <select value={form.kitchen_id || ""} onChange={e => setForm(f => ({ ...f, kitchen_id: e.target.value }))}
                  className="w-full border border-[#E5E1DB] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#E8956D]">
                  <option value="">— Select Kitchen —</option>
                  {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
                <textarea rows={2} value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes..." className="w-full border border-[#E5E1DB] rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" />
              </div>
              {err && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{err}</p>}
              <div className="flex gap-3">
                <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSave} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-lg py-2.5 text-sm font-bold hover:bg-[#D48661]">
                  {modal.mode === "create" ? "Create Route" : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── VEHICLES & DRIVERS TAB ──────────────────────────────────────────────────
function VehiclesDriversTab({ currentUser }) {
  const canEdit = !!(currentUser?.is_admin || currentUser?.permissions?.vehicle_master_edit);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [subTab, setSubTab] = useState("vehicles");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const VEHICLE_TYPES = ["tempo", "van", "truck", "motorcycle", "other"];
  const OWN_TYPES = ["trust", "contractor", "temporary_rental"];

  const fetchVehicles = () =>
    logisticsEntitiesAPI.getVehicles().then(r => setVehicles(r.data || [])).catch(() => {});
  const fetchDrivers = () =>
    logisticsEntitiesAPI.getDrivers().then(r => setDrivers(r.data || [])).catch(() => {});

  useEffect(() => { fetchVehicles(); fetchDrivers(); }, []); // eslint-disable-line

  const openCreate = () => {
    setForm({ registration_number: "", vehicle_type: "tempo", ownership: "trust", owner_name: "", owner_contact: "", notes: "", status: "active" });
    setEditId(null); setShowForm(true); setErr("");
  };
  const openEdit = (v) => {
    setForm({ ...v }); setEditId(v.id); setShowForm(true); setErr("");
  };
  const handleSave = async () => {
    setLoading(true); setErr("");
    try {
      if (editId) await logisticsEntitiesAPI.updateVehicle(editId, form);
      else await logisticsEntitiesAPI.createVehicle(form);
      setShowForm(false); setMsg("Saved"); fetchVehicles();
    } catch (e) { setErr(formatError(e)); }
    setLoading(false);
  };
  const handleRetire = async (id) => {
    if (!window.confirm("Retire this vehicle?")) return;
    await logisticsEntitiesAPI.retireVehicle(id).catch(() => {});
    fetchVehicles();
  };

  return (
    <div className="space-y-4" data-testid="vehicles-drivers-tab">
      <div className="flex gap-2 border-b border-[#E5E1DB]">
        {["vehicles", "drivers"].map(t => (
          <button key={t} onClick={() => setSubTab(t)}
            className={`px-4 py-2 text-sm font-semibold capitalize -mb-px ${subTab === t ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73]"}`}>
            {t}
          </button>
        ))}
      </div>

      {msg && <div className="text-sm text-[#637C64] bg-[#EDF2EE] px-4 py-2 rounded-lg">{msg}</div>}
      {err && <div className="text-sm text-[#C2624E] bg-[#FBEAE7] px-4 py-2 rounded-lg">{err}</div>}

      {/* VEHICLES sub-tab */}
      {subTab === "vehicles" && (
        <div className="space-y-3">
          {canEdit && (
            <div className="flex justify-end">
              <button onClick={openCreate} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1">
                <Plus size={14} /> Add Vehicle
              </button>
            </div>
          )}

          {showForm && subTab === "vehicles" && (
            <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-3">
              <h3 className="font-bold text-[#3A322C] text-sm">{editId ? "Edit Vehicle" : "New Vehicle"}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Registration No. *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] font-mono uppercase"
                    placeholder="e.g. TS07UB0497" value={form.registration_number || ""}
                    onChange={e => setForm(f => ({ ...f, registration_number: e.target.value.toUpperCase() }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Type</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.vehicle_type || "tempo"} onChange={e => setForm(f => ({ ...f, vehicle_type: e.target.value }))}>
                    {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Ownership</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.ownership || "trust"} onChange={e => setForm(f => ({ ...f, ownership: e.target.value }))}>
                    {OWN_TYPES.map(o => <option key={o} value={o}>{o.replace(/_/g," ")}</option>)}
                  </select></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Owner / Contractor Name</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.owner_name || ""} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Contact</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.owner_contact || ""} onChange={e => setForm(f => ({ ...f, owner_contact: e.target.value }))} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Capacity</label>
                  <input type="number" min="0" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.capacity || ""} onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || null }))} /></div>
              </div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
              <div className="flex gap-3">
                <button onClick={() => setShowForm(false)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={handleSave} disabled={loading} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2 text-sm font-bold disabled:opacity-60">
                  {loading ? "Saving…" : "Save Vehicle"}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F3F0]">
                  <tr>{["Reg. Number","Type","Ownership","Owner","Contact","Status",""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147] whitespace-nowrap">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-[#F4F3F0]">
                  {vehicles.filter(v => v.status !== "inactive").map(v => (
                    <tr key={v.id} className="hover:bg-[#FAFAF8]">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-[#3A322C]">{v.registration_number}</td>
                      <td className="px-4 py-3 capitalize text-[#5C5147]">{v.vehicle_type}</td>
                      <td className="px-4 py-3 text-xs capitalize text-[#8A7E73]">{(v.ownership || "trust").replace(/_/g," ")}</td>
                      <td className="px-4 py-3 text-[#5C5147]">{v.owner_name || "—"}</td>
                      <td className="px-4 py-3 text-[#8A7E73]">{v.owner_contact || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${v.status === "active" ? "bg-green-100 text-green-700" : "bg-[#F4F3F0] text-[#8A7E73]"}`}>{v.status}</span>
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        {canEdit && <><button onClick={() => openEdit(v)} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                        <button onClick={() => handleRetire(v.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button></>}
                      </td>
                    </tr>
                  ))}
                  {vehicles.filter(v => v.status !== "inactive").length === 0 && (
                    <tr><td colSpan={7} className="text-center py-10 text-[#8A7E73]">No active vehicles. Run "Import Vehicles & Drivers" from the Reconciliation tab.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* DRIVERS sub-tab */}
      {subTab === "drivers" && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Name","Phone","License","Status"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {drivers.filter(d => d.status !== "inactive").map(d => (
                  <tr key={d.id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3 font-bold text-[#3A322C]">{d.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#5C5147]">{d.phone || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#8A7E73]">{d.license_number || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${d.status === "active" ? "bg-green-100 text-green-700" : "bg-[#F4F3F0] text-[#8A7E73]"}`}>{d.status}</span>
                    </td>
                  </tr>
                ))}
                {drivers.filter(d => d.status !== "inactive").length === 0 && (
                  <tr><td colSpan={4} className="text-center py-10 text-[#8A7E73]">No drivers. Import from the Reconciliation tab first.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── TEMP ASSIGNMENTS TAB (redesigned) ────────────────────────────────────────
function TempAssignmentsTab({ routes, coordinators, currentUser }) {
  const canEdit = currentUser?.is_admin || !!currentUser?.permissions?.temp_assignment_edit || !!currentUser?.permissions?.route_assignment_edit;
  const [subTab, setSubTab] = useState("coordinator");

  // Get coordinator names from the master
  const coordNames = coordinators.map(c => c.coordinator_name || c.user_name || "").filter(Boolean).sort();

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-[#F4F3F0] p-1 rounded-xl w-fit">
        {[["coordinator", "Temporary Coordinator"], ["vehicle", "Temporary Vehicle"]].map(([id, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${subTab === id ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {label}
          </button>
        ))}
      </div>

      {subTab === "coordinator" && (
        <TempCoordinatorSubTab routes={routes} coordNames={coordNames} canEdit={canEdit} />
      )}
      {subTab === "vehicle" && (
        <TempVehicleSubTab routes={routes} canEdit={canEdit} />
      )}
    </div>
  );
}

// ─── B1: TEMP COORDINATOR SUB-TAB ─────────────────────────────────────────────
function TempCoordinatorSubTab({ routes = [], coordNames = [], canEdit }) {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [absentCoord, setAbsentCoord] = useState("");
  const [routeCoverage, setRouteCoverage] = useState({}); // routeId → {covering_coordinator, no_service}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  // Get routes assigned to the absent coordinator — defensive: handle undefined/null
  const safeRoutes = Array.isArray(routes) ? routes : [];
  const safeCoordNames = Array.isArray(coordNames) ? coordNames : [];
  const absentRoutes = safeRoutes.filter(r =>
    r && !r.is_retired && (r.coordinator_name || "").toUpperCase() === (absentCoord || "").toUpperCase()
  );

  // Count coverage
  const covered = absentRoutes.filter(r => r && (routeCoverage[r.id]?.covering_coordinator || routeCoverage[r.id]?.no_service)).length;
  const uncovered = absentRoutes.length - covered;

  useEffect(() => {
    if (date) {
      logisticsAPI.getTempCoordAssignments({ date })
        .then(r => setHistory(r.data || []))
        .catch(() => {});
    }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!date || !absentCoord) { setErr("Select date and absent coordinator"); return; }
    if (uncovered > 0) { setErr(`${uncovered} route(s) still uncovered. Assign a covering coordinator or mark as "No Service".`); return; }
    setSaving(true); setErr("");
    try {
      const routeAssignments = absentRoutes.map(r => ({
        route_id: r.id,
        route_number: r.route_number,
        covering_coordinator: routeCoverage[r.id]?.covering_coordinator || "",
        no_service: !!routeCoverage[r.id]?.no_service,
      }));
      await logisticsAPI.createTempCoordAssignment({ date, absent_coordinator: absentCoord, route_assignments: routeAssignments });
      setMsg(`Saved! ${absentCoord} is covered on ${date}`);
      setRouteCoverage({}); setAbsentCoord("");
      const r = await logisticsAPI.getTempCoordAssignments({ date });
      setHistory(r.data || []);
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-5 max-w-4xl">
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-2.5 text-sm flex items-center gap-2"><CheckCircle size={13}/>{msg}</div>}
      {err && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-2.5 text-sm">{err}<button onClick={() => setErr("")} className="ml-2">×</button></div>}

      {canEdit && (
        <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-4">
          <h3 className="font-bold text-[#3A322C]">Mark Coordinator as Absent</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label>
              <input type="date" value={date} onChange={e => { setDate(e.target.value); setRouteCoverage({}); }}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Absent Coordinator *</label>
              <select value={absentCoord} onChange={e => { setAbsentCoord(e.target.value); setRouteCoverage({}); }}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]">
                <option value="">— Select coordinator —</option>
                {safeCoordNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {absentCoord && (
            <>
              {/* Coverage banner */}
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${uncovered > 0 ? "bg-[#FBEAE7] border border-[#C2624E]/20" : "bg-[#EDF2EE] border border-[#637C64]/20"}`}>
                <AlertTriangle size={16} className={uncovered > 0 ? "text-[#C2624E]" : "text-[#637C64]"} />
                <span className={`text-sm font-bold ${uncovered > 0 ? "text-[#C2624E]" : "text-[#637C64]"}`}>
                  {uncovered > 0 ? `${uncovered} of ${absentRoutes.length} routes still uncovered` : `All ${absentRoutes.length} routes covered`}
                </span>
              </div>

              {/* Per-route coverage */}
              {absentRoutes.length === 0 ? (
                <p className="text-sm text-[#8A7E73] text-center py-4">No routes assigned to {absentCoord}</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase text-[#8A7E73]">Assign cover for each route</p>
                  {absentRoutes.map(r => (
                    <div key={r.id} className="flex items-center gap-3 bg-[#FAFAF8] border border-[#E5E1DB] rounded-xl px-4 py-3">
                      <span className="font-bold text-[#E8956D] w-20 shrink-0">Route {r.route_number}</span>
                      <label className="flex items-center gap-1.5 text-xs text-[#8A7E73] cursor-pointer shrink-0">
                        <input type="checkbox"
                          checked={!!routeCoverage[r.id]?.no_service}
                          onChange={e => setRouteCoverage(p => ({ ...p, [r.id]: { no_service: e.target.checked, covering_coordinator: "" } }))}
                          className="accent-[#E8956D]" />
                        No Service
                      </label>
                      {!routeCoverage[r.id]?.no_service && (
                        <select
                          value={routeCoverage[r.id]?.covering_coordinator || ""}
                          onChange={e => setRouteCoverage(p => ({ ...p, [r.id]: { covering_coordinator: e.target.value, no_service: false } }))}
                          className="border border-[#E5E1DB] rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#E8956D] min-w-[140px]">
                          <option value="">— Cover with... —</option>
                          {safeCoordNames.filter(n => n !== absentCoord).map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      )}
                      {(routeCoverage[r.id]?.covering_coordinator || routeCoverage[r.id]?.no_service) && (
                        <CheckCircle size={15} className="text-[#637C64] shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving || uncovered > 0}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-6 py-2.5 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
                  {saving ? <Loader size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                  Save Coverage Plan
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
            <p className="font-bold text-[#3A322C] text-sm">Coverage for {date}</p>
          </div>
          {history.map(h => (
            <div key={h.id} className="px-5 py-3 border-b border-[#F4F3F0] last:border-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{h.absent_coordinator} (absent)</span>
                <button onClick={async () => { await logisticsAPI.deleteTempCoordAssignment(h.id); const r = await logisticsAPI.getTempCoordAssignments({ date }); setHistory(r.data || []); }}
                  className="text-[#C4BAAF] hover:text-[#C2624E]"><Trash2 size={13} /></button>
              </div>
              <div className="mt-2 space-y-1">
                {(h.route_assignments || []).map(ra => (
                  <div key={ra.route_id} className="flex items-center gap-2 text-xs">
                    <span className="font-bold text-[#E8956D]">R{ra.route_number}</span>
                    {ra.no_service ? <span className="text-[#C2624E]">No Service</span> : <span className="text-[#637C64] font-bold">{ra.covering_coordinator}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── B2: TEMP VEHICLE SUB-TAB ─────────────────────────────────────────────────
function TempVehicleSubTab({ routes, canEdit }) {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ replacement_vehicle_id: "", replacement_driver_id: "", reason: "maintenance", notes: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const REASONS = ["maintenance", "breakdown", "other"];

  useEffect(() => {
    logisticsEntitiesAPI.getVehicles({ status: "active" }).then(r => setVehicles(r.data || [])).catch(() => {});
    logisticsEntitiesAPI.getDrivers({ status: "active" }).then(r => setDrivers(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (date) {
      logisticsAPI.getTempVehicleAssignments({ date })
        .then(r => setHistory(r.data || []))
        .catch(() => {});
    }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRoute = routes.find(r => r.id === selectedRouteId);

  const handleSave = async () => {
    if (!date || !selectedRouteId || !form.replacement_vehicle_id) {
      setErr("Select date, route, and replacement vehicle");
      return;
    }
    setSaving(true); setErr("");
    try {
      await logisticsAPI.createTempVehicleAssignment({
        ...form,
        date,
        route_id: selectedRouteId,
        route_number: selectedRoute?.route_number || "",
      });
      setMsg(`Temp vehicle saved for Route ${selectedRoute?.route_number} on ${date}`);
      setForm({ replacement_vehicle_id: "", replacement_driver_id: "", reason: "maintenance", notes: "" });
      const r = await logisticsAPI.getTempVehicleAssignments({ date });
      setHistory(r.data || []);
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatError(e)); }
    setSaving(false);
  };

  const permVehicle = vehicles.find(v => v.id === (selectedRoute?.assigned_vehicle_id || ""));

  return (
    <div className="space-y-5 max-w-3xl">
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-2.5 text-sm flex items-center gap-2"><CheckCircle size={13}/>{msg}</div>}
      {err && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-2.5 text-sm">{err}<button onClick={() => setErr("")} className="ml-2">×</button></div>}

      {canEdit && (
        <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-4">
          <h3 className="font-bold text-[#3A322C]">Assign Replacement Vehicle</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route *</label>
              <select value={selectedRouteId} onChange={e => setSelectedRouteId(e.target.value)}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]">
                <option value="">— Select route —</option>
                {routes.filter(r => !r.is_retired).map(r => (
                  <option key={r.id} value={r.id}>Route {r.route_number}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedRoute && (
            <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs">
              <span className="text-[#8A7E73]">Permanent vehicle: </span>
              <span className="font-bold text-[#3A322C]">{selectedRoute.assigned_vehicle || selectedRoute.driver_vehicle_number || "—"}</span>
              <span className="text-[#8A7E73] ml-3">Driver: </span>
              <span className="font-bold text-[#3A322C]">{selectedRoute.driver_name || "—"}</span>
              {(selectedRoute.assigned_vehicle || selectedRoute.driver_vehicle_number) && (
                <span className="ml-3 text-[#D98032] font-bold">⚠ Will be marked unavailable on {date}</span>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Replacement Vehicle *</label>
              <select value={form.replacement_vehicle_id} onChange={e => setForm(f => ({ ...f, replacement_vehicle_id: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]">
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.registration_number} ({v.vehicle_type})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Driver (optional)</label>
              <select value={form.replacement_driver_id} onChange={e => setForm(f => ({ ...f, replacement_driver_id: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]">
                <option value="">— Select driver —</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason *</label>
              <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]">
                {REASONS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="Optional..." />
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleSave} disabled={saving || !form.replacement_vehicle_id || !selectedRouteId}
              className="bg-[#E8956D] text-[#3A322C] rounded-lg px-6 py-2.5 text-sm font-bold disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader size={13} className="animate-spin" /> : <Truck size={13} />}
              Assign Replacement
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
            <p className="font-bold text-[#3A322C] text-sm">Temp Vehicles for {date}</p>
          </div>
          {history.map(h => (
            <div key={h.id} className="flex items-center gap-3 px-5 py-3 border-b border-[#F4F3F0] last:border-0">
              <span className="font-bold text-[#E8956D] w-20 shrink-0">Route {h.route_number}</span>
              <span className="font-mono text-xs bg-[#F4F3F0] px-2 py-0.5 rounded font-bold text-[#637C64]">
                {h.replacement_vehicle_number || h.replacement_vehicle_id?.slice(-6)}
              </span>
              <span className="text-xs text-[#8A7E73] capitalize">{h.reason}</span>
              <button onClick={async () => { await logisticsAPI.deleteTempVehicleAssignment(h.id); const r = await logisticsAPI.getTempVehicleAssignments({ date }); setHistory(r.data || []); }}
                className="text-[#C4BAAF] hover:text-[#C2624E] ml-auto"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── RECONCILIATION TAB ───────────────────────────────────────────────────────
function ReconciliationTab({ currentUser }) {
  const canEdit = !!(currentUser?.is_admin || currentUser?.permissions?.route_assignment_edit);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [running, setRunning] = useState("");
  const [result, setResult] = useState(null);

  const run = async (label, fn) => {
    setRunning(label); setErr(""); setMsg(""); setResult(null);
    try {
      const r = await fn();
      setMsg(r.data?.message || "Done!");
      setResult(r.data);
    } catch (e) { setErr(formatError(e)); }
    setRunning("");
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="reconciliation-tab">
      <div>
        <h2 className="font-bold text-[#3A322C] text-lg">Data Reconciliation</h2>
        <p className="text-sm text-[#8A7E73]">Admin tools to clean and sync logistics data.</p>
      </div>

      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm">✓ {msg}</div>}
      {err && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{err}</div>}

      {result && (
        <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs space-y-1 text-[#5C5147]">
          {Object.entries(result).filter(([k]) => k !== "message").map(([k, v]) => (
            <p key={k}><span className="font-bold">{k}:</span> {String(v)}</p>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="space-y-3">
          {/* Sync school route numbers from Route Master */}
          <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-3">
            <div>
              <h3 className="font-bold text-[#3A322C]">Sync School Routes from Route Master</h3>
              <p className="text-sm text-[#8A7E73] mt-1">
                Clears old embedded route numbers from School Master, then re-populates them
                from the Route Master (routes.stops). Schools not in any route's stop list
                will have no route number. Use this to remove legacy route data.
              </p>
            </div>
            <button
              onClick={() => run("sync", logisticsAPI.syncSchoolRoutesFromMaster)}
              disabled={!!running}
              className="bg-[#637C64] text-white rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-60 flex items-center gap-2 hover:bg-[#4F6450]"
              data-testid="sync-school-routes-btn"
            >
              {running === "sync" ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {running === "sync" ? "Syncing…" : "Sync School Route Numbers"}
            </button>
          </div>

          {/* Import vehicles/drivers from routes */}
          <div className="bg-white border border-[#E5E1DB] rounded-2xl p-5 space-y-3">
            <div>
              <h3 className="font-bold text-[#3A322C]">Import Vehicles & Drivers from Routes</h3>
              <p className="text-sm text-[#8A7E73] mt-1">
                Auto-create Vehicle and Driver master records from vehicle numbers and driver names
                embedded in the old routes data.
              </p>
            </div>
            <button
              onClick={() => run("vehicles", logisticsEntitiesAPI.importVehiclesFromRoutes)}
              disabled={!!running}
              className="bg-[#E8956D] text-[#3A322C] rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-60 flex items-center gap-2 hover:bg-[#D48661]"
            >
              {running === "vehicles" ? <Loader size={14} className="animate-spin" /> : <Truck size={14} />}
              {running === "vehicles" ? "Importing…" : "Import Vehicles & Drivers"}
            </button>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="bg-[#F4F3F0] rounded-xl px-5 py-4 text-sm text-[#8A7E73]">
          Admin or route_assignment_edit permission required to run reconciliation actions.
        </div>
      )}
    </div>
  );
}

